// added-video-player
import React, { useEffect, useState, useRef, FC, useCallback } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Box,
  CircularProgress,
  Alert,
  TableHead,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Link,
  Autocomplete,
  LinearProgress,
  DialogContentText,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { chartDataService, Video } from '../services/chartDataService';
import { useNavigate, useLocation } from 'react-router-dom';
import { youtubeService } from '../services/youtubeService';
import { ScriptManager } from './ScriptManager';
import { YouTubeSearchDialog } from './YouTubeSearchDialog';
import { SeekTimeDialog } from './SeekTimeDialog';
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'; // Added CloudDownloadIcon
import EditIcon from '@mui/icons-material/Edit';
import { useSnackbar } from 'notistack';
import VideoFramingEditor from './VideoFramingEditor';
import { SERVER_BASE_URL } from '../config';
import { splitAtDelimiter, delimiterToNewline, newlineToDelimiter } from '../utils/textSplitting';
import { authedEventSource } from '../lib/authedEventSource';

// Master Video List column order (the `videos` DB table rendered as a table).
// Order matches the columns the user has always seen in the Master Video List.
const MASTER_LIST_HEADERS = [
  'Song',
  'Artist',
  'S3 URL',
  'YouTube Link',
  'In Point',
  'Duration',
  'Zoom',
  'Vertical URL',
  'Seek In',
] as const;

// Map each Master List column to its Video field name (for typed DB writes).
// Keyed by the lower-cased header substring used in handleCellSave.
const HEADER_TO_VIDEO_FIELD: Record<string, keyof Omit<Video, 'id'>> = {
  'song': 'song',
  'artist': 'artist',
  's3 url': 's3Url',
  'youtube link': 'youtubeLink',
  'in point': 'inPoint',
  'duration': 'duration',
  'zoom': 'zoom',
  'vertical url': 'verticalUrl',
  'seek in': 'seekIn',
};

// Resolve a header string to its Video field name, or null if not whitelisted.
const headerToVideoField = (header: string): keyof Omit<Video, 'id'> | null => {
  const name = (header || '').toLowerCase();
  const matchKey = Object.keys(HEADER_TO_VIDEO_FIELD).find(k => name.includes(k));
  return matchKey ? HEADER_TO_VIDEO_FIELD[matchKey] : null;
};

// Build the ordered cell-row (string[]) for a Video, matching MASTER_LIST_HEADERS.
const videoToRowValues = (video: Video): string[] =>
  MASTER_LIST_HEADERS.map(header => {
    const field = headerToVideoField(header);
    return field ? String((video[field] ?? '')).trim() : '';
  });

interface Cell {
  value: string | undefined;
  isEditing: boolean;
}

interface Row {
  cells: Cell[];
  isNew: boolean;
  videoId?: number; // DB id of the underlying `videos` row (for update/delete)
}

interface ParsedScript {
  intro: string;
  songs: string[];
  outro: string;
}

export const VideoManager: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Auth is handled by the app-level AuthGate (Supabase); the Master Video List
  // is a typed DB table, so this component is always "signed in" once mounted.
  const [isSignedIn] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'song', direction: 'asc' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<number | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [cellToFocus, setCellToFocus] = useState<{row: number, col: number} | null>(null);
  const [videoPlayerDialogOpen, setVideoPlayerDialogOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');
  const [currentInPoint, setCurrentInPoint] = useState('0');
  const [currentSeekIn, setCurrentSeekIn] = useState('0');
  const [currentDuration, setCurrentDuration] = useState('0'); // Add state for duration
  const [currentRowIndex, setCurrentRowIndex] = useState(-1);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewTimeout, setPreviewTimeout] = useState<NodeJS.Timeout | null>(null);
  const [scriptDialogOpen, setScriptDialogOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchInitialQuery, setSearchInitialQuery] = useState('');
  const [searchTargetRowIndex, setSearchTargetRowIndex] = useState<number | null>(null);
  const [highlightedRow, setHighlightedRow] = useState<number | null>(null);
  const highlightedRowRef = useRef<HTMLTableRowElement>(null);
  const [editingS3UrlState, setEditingS3UrlState] = useState<{ row: number; col: number; url: string; song?: string; artist?: string; currentYoutubeUrl?: string; inPoint?: string; duration?: string; } | null>(null); // State for S3 URL edit dialog
  const [openedFromCharts, setOpenedFromCharts] = useState(false); // Track if opened from charts
  const [editingVerticalUrlState, setEditingVerticalUrlState] = useState<{ row: number; col: number; url: string; song?: string; artist?: string; inPoint?: string; duration?: string; } | null>(null); // Add state for Vertical URL edit dialog
  const [verticalVideoUploadFile, setVerticalVideoUploadFile] = useState<File | null>(null); // State for file upload
  const [verticalUploadProgress, setVerticalUploadProgress] = useState<number>(0); // Upload progress
  // Add a state to track video loading status
  const [videoLoadError, setVideoLoadError] = useState<boolean>(false);
  const [videoSignedUrl, setVideoSignedUrl] = useState<string>(''); // Add this state for the signed URL
  const [isLoadingSignedUrl, setIsLoadingSignedUrl] = useState<boolean>(false); // Add this state to track loading
  // Add state for main upload progress tracking
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [currentUploadId, setCurrentUploadId] = useState<string | null>(null);

  // Column resize state
  const COLUMN_WIDTHS_STORAGE_KEY = 'videoManagerColumnWidths';
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    // Load from localStorage on init
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(COLUMN_WIDTHS_STORAGE_KEY);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          console.error('Failed to parse saved column widths:', e);
        }
      }
    }
    return {};
  });
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const [resizeStartX, setResizeStartX] = useState<number>(0);
  const [resizeStartWidth, setResizeStartWidth] = useState<number>(0);
  const [minColumnWidths, setMinColumnWidths] = useState<Record<string, number>>({});
  const headerRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
  const verticalVideoRef = useRef<HTMLVideoElement>(null); // Add ref for vertical video element
  const [verticalVideoCurrentTime, setVerticalVideoCurrentTime] = useState<number>(0); // Track current time for timeline
  const animationFrameRef = useRef<number | null>(null);
  const [verticalVideoDuration, setVerticalVideoDuration] = useState<number>(0); // Track total video duration

  // Horizontal video timer state
  const horizontalVideoRef = useRef<HTMLVideoElement>(null);
  const [horizontalVideoCurrentTime, setHorizontalVideoCurrentTime] = useState<number>(0);
  const horizontalAnimationFrameRef = useRef<number | null>(null);

  // Smooth timer update function
  const updateTimer = useCallback(() => {
    if (verticalVideoRef.current && !verticalVideoRef.current.paused) {
      setVerticalVideoCurrentTime(verticalVideoRef.current.currentTime);
      animationFrameRef.current = requestAnimationFrame(updateTimer);
    }
  }, []);

  // Start/stop smooth timer updates
  const startSmoothTimer = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    animationFrameRef.current = requestAnimationFrame(updateTimer);
  }, [updateTimer]);

  const stopSmoothTimer = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  // Horizontal video timer update functions
  const updateHorizontalTimer = useCallback(() => {
    if (horizontalVideoRef.current && !horizontalVideoRef.current.paused) {
      setHorizontalVideoCurrentTime(horizontalVideoRef.current.currentTime);
      horizontalAnimationFrameRef.current = requestAnimationFrame(updateHorizontalTimer);
    }
  }, []);

  const startHorizontalSmoothTimer = useCallback(() => {
    if (horizontalAnimationFrameRef.current) {
      cancelAnimationFrame(horizontalAnimationFrameRef.current);
    }
    horizontalAnimationFrameRef.current = requestAnimationFrame(updateHorizontalTimer);
  }, [updateHorizontalTimer]);

  const stopHorizontalSmoothTimer = useCallback(() => {
    if (horizontalAnimationFrameRef.current) {
      cancelAnimationFrame(horizontalAnimationFrameRef.current);
      horizontalAnimationFrameRef.current = null;
    }
  }, []);

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (horizontalAnimationFrameRef.current) {
        cancelAnimationFrame(horizontalAnimationFrameRef.current);
      }
    };
  }, []);
  const [verticalVideoSessionId, setVerticalVideoSessionId] = useState<string>(''); // Track video session to prevent unnecessary reloads
  // Add state variables for S3 video upload (next to the vertical video upload state variables)
  const [s3VideoUploadFile, setS3VideoUploadFile] = useState<File | null>(null); // State for S3 file upload
  const [s3UploadProgress, setS3UploadProgress] = useState<number>(0); // Upload progress for S3
  
  // Video framing editor state
  const [framingEditorOpen, setFramingEditorOpen] = useState<boolean>(false);
  const [framingEditorData, setFramingEditorData] = useState<{
    videoUrl: string;
    horizontalUrl?: string;
    inPoint: number;
    duration: number;
  } | null>(null);

  // State for filename confirmation dialog
  const [isFileNameConfirmationDialogOpen, setIsFileNameConfirmationDialogOpen] = useState(false);
  const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);
  const [currentUploadFileName, setCurrentUploadFileName] = useState<string>('');
  const [uploadTargetRowInfo, setUploadTargetRowInfo] = useState<{ rowIndex: number; s3Key?: string } | null>(null);
  const [isUploadingS3, setIsUploadingS3] = useState<boolean>(false); // New state for upload in progress
  const [dialogInputYoutubeUrl, setDialogInputYoutubeUrl] = useState<string>(''); // State for YouTube URL input in S3 dialog
  // const [isS3DialogReadyForAction, setIsS3DialogReadyForAction] = useState<boolean>(false); // Flag for S3 dialog actions - REMOVED

  // State for SeekTimeDialog
  const [seekTimeDialogOpen, setSeekTimeDialogOpen] = useState<boolean>(false);
  const [seekTimeDialogVideoUrl, setSeekTimeDialogVideoUrl] = useState<string>('');
  const [seekTimeDialogRowIndex, setSeekTimeDialogRowIndex] = useState<number>(-1);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    console.log(`[VideoManager] videoPlayerDialogOpen state changed to: ${videoPlayerDialogOpen}`);
  }, [videoPlayerDialogOpen]);

  // Handle navigation state for highlighting rows
  useEffect(() => {
    if (location.state && typeof location.state === 'object' && 'highlightRow' in location.state) {
      const { highlightRow } = location.state as { highlightRow: number };
      console.log('[VideoManager] Setting highlighted row from navigation:', highlightRow);
      setHighlightedRow(highlightRow);
      // Clear the state to prevent re-highlighting on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  useEffect(() => {
    if (!rows.length || !headers.length) return;

    const params = new URLSearchParams(location.search);
    const songParam = params.get('song');
    const artistParam = params.get('artist');
    const playerParam = params.get('player');
    const s3UrlParam = params.get('s3url'); // Get S3 URL from params
    const hasPlayerParam = playerParam === 'true'; // Define hasPlayerParam from playerParam

    console.log(`[VideoManager] URL Params Check - song: "${songParam}", artist: "${artistParam}", player: "${playerParam}", s3url: "${s3UrlParam}"`);
    console.log(`[VideoManager] Available headers:`, headers);

    // Check if hasPlayerParam is true before proceeding with player logic
    if (hasPlayerParam) {
      if (s3UrlParam && !videoPlayerDialogOpen) {
        // Logic for direct S3 URL
        console.log(`[VideoManager] Direct S3 URL provided: "${s3UrlParam}"`);
        // Find In Point from params or default
        const inPointParam = params.get('in_point') || '0'; // Assuming in_point might be passed

        // Set state and open player
        setCurrentVideoUrl(s3UrlParam);
        setCurrentInPoint(inPointParam);
        setCurrentDuration('0'); // Default Duration
        setCurrentRowIndex(-1); // Indicate we didn't necessarily find a row index

        setTimeout(() => {
            console.log('[VideoManager] Opening video player dialog NOW with direct S3 URL:', s3UrlParam);
            setVideoPlayerDialogOpen(true);
            // Clear URL parameters after opening dialog
            setTimeout(() => {
                console.log('[VideoManager] Clearing URL parameters (direct S3 URL)');
                navigate('/video-manager', { replace: true });
            }, 200);
        }, 100);

        return; // Stop further processing if we handled direct S3 URL
      } else if (songParam && artistParam && !videoPlayerDialogOpen) {
        // Logic for finding row by song/artist
        console.log(`[VideoManager] Looking for song: "${songParam}" by artist: "${artistParam}"`);

        const songColumnIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
        const artistColumnIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));

        if (songColumnIndex === -1 || artistColumnIndex === -1) {
          console.warn('[VideoManager] Cannot locate song/artist columns');
          navigate('/video-manager', { replace: true }); // Clear params if columns missing
          return;
        }

        console.log(`[VideoManager] Song column index: ${songColumnIndex}, Artist column index: ${artistColumnIndex}`);

        const matchingRowIndex = rows.findIndex(row => {
          if (row.isNew) return false;
          const rowSong = (row.cells[songColumnIndex]?.value || '').toLowerCase().trim();
          const rowArtist = (row.cells[artistColumnIndex]?.value || '').toLowerCase().trim();
          const paramSong = songParam.toLowerCase().trim();
          const paramArtist = artistParam.toLowerCase().trim();
          const songMatch = rowSong.includes(paramSong) || paramSong.includes(rowSong);
          const artistMatch = rowArtist.includes(paramArtist) || paramArtist.includes(rowArtist);
          console.log(`[VideoManager] Comparing - Row: "${rowSong}" / "${rowArtist}" to Param: "${paramSong}" / "${paramArtist}" - Match: ${songMatch && artistMatch}`);
          return songMatch && artistMatch;
        });

        if (matchingRowIndex !== -1) {
          console.log(`[VideoManager] Found matching row at index ${matchingRowIndex}`);
          setHighlightedRow(matchingRowIndex);

          const s3LinkColIndex = headers.findIndex(h => h.toLowerCase().includes('s3 url'));
          console.log(`[VideoManager] S3 URL column index: ${s3LinkColIndex}`);

          if (s3LinkColIndex !== -1) {
            const s3Url = rows[matchingRowIndex].cells[s3LinkColIndex]?.value || '';
            console.log(`[VideoManager] S3 URL found: "${s3Url}"`);

            if (s3Url) {
              console.log('[VideoManager] Setting current row and S3 video URL');
              const inPointColIndex = headers.findIndex(h => h.toLowerCase().includes('in point'));
              const durationColIndex = headers.findIndex(h => h.toLowerCase().includes('duration'));
              const inPointValue = inPointColIndex !== -1 ? rows[matchingRowIndex].cells[inPointColIndex]?.value || '0' : '0';
              const durationValue = durationColIndex !== -1 ? rows[matchingRowIndex].cells[durationColIndex]?.value || '0' : '0'; // Get duration

              setTimeout(() => {
                setCurrentRowIndex(matchingRowIndex); // Set the found index
                setCurrentVideoUrl(s3Url);
                setCurrentInPoint(inPointValue);
                setCurrentDuration(durationValue); // Set duration state

                setTimeout(() => {
                  console.log('[VideoManager] Opening video player dialog NOW with S3 URL:', s3Url);
                  setVideoPlayerDialogOpen(true);
                  setTimeout(() => {
                    console.log('[VideoManager] Clearing URL parameters (found S3 URL)');
                    navigate('/video-manager', { replace: true });
                  }, 200);
                }, 100);
              }, 100);
            } else {
              console.warn('[VideoManager] No S3 URL found for the matching row');
              navigate('/video-manager', { replace: true });
            }
          } else {
            console.warn('[VideoManager] S3 Link column not found');
            navigate('/video-manager', { replace: true });
          }
        } else {
          console.log(`[VideoManager] No matching row found for "${songParam}" by "${artistParam}"`);
          navigate('/video-manager', { replace: true });
        }
      }
    } else if (location.search) {
      // If playerParam is not true or other params exist without player=true
      console.log('[VideoManager] Clearing non-player or other URL parameters');
      navigate('/video-manager', { replace: true });
    }
  }, [rows, headers, location.search, navigate, videoPlayerDialogOpen]); // Add videoPlayerDialogOpen dependency

  useEffect(() => {
    if (highlightedRow !== null && highlightedRowRef.current) {
      highlightedRowRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      const timer = setTimeout(() => {
        setHighlightedRow(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [highlightedRow]);

  useEffect(() => {
    if (cellToFocus) {
      const input = document.querySelector(`[data-row="${cellToFocus.row}"][data-col="${cellToFocus.col}"] input`);
      if (input) {
        (input as HTMLInputElement).focus();
        setCellToFocus(null);
      }
    }
  }, [cellToFocus]); // Added missing dependency

  useEffect(() => {
    if (editingS3UrlState && !editingS3UrlState.currentYoutubeUrl) {
      setDialogInputYoutubeUrl(''); // Reset when dialog opens and no initial YT URL
    }
  }, [editingS3UrlState]);

  // --- Define Handlers First ---
  const handleMarkInPoint = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    if (!isPreviewing) {
      const video = e.target as HTMLVideoElement;
      const newInPoint = video.currentTime.toFixed(1);
      setCurrentInPoint(newInPoint);
      if (currentRowIndex !== -1) {
        const inPointColIndex = headers.findIndex(h => h.toLowerCase().includes('in point'));
        if (inPointColIndex !== -1) {
          setRows(prevRows => {
            const newRows = [...prevRows];
            const dataRowIndex = currentRowIndex;
            if (newRows[dataRowIndex]?.cells[inPointColIndex]) {
              newRows[dataRowIndex].cells[inPointColIndex].value = newInPoint;
            }
            return newRows;
          });
      }
      }
    }
  };

  const handleInPointChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Corrected regex: use single backslashes for RegExp literals
    if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) { 
      setCurrentInPoint(value); // Update local state for the dialog field
      // Do NOT directly update rows state here, wait for Save button
    }
  };

  const handleSeekInChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Corrected regex: use single backslashes for RegExp literals
    if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) { 
      setCurrentSeekIn(value); // Update local state for the dialog field
      // Do NOT directly update rows state here, wait for Save button
    }
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Corrected regex: use single backslashes for RegExp literals
    if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) { 
      setCurrentDuration(value); // Update local state for the dialog field
      // Do NOT directly update rows state here, wait for Save button
    }
  };

  const handleScriptSave = (parsedScript: ParsedScript) => {
    console.log('[VideoManager] Script saved (not implemented):', parsedScript);
    setScriptDialogOpen(false);
  };
  // --- End Define Handlers ---


  // Function to load the Master Video List from the DB (`videos` table).
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // listVideos() returns the whole list already sorted by song/artist.
      const videos = await chartDataService.listVideos();
      console.log(`[VideoManager] Fetched ${videos.length} videos from DB`);

      // Fixed Master List column order (replaces the old sheet header row).
      const fetchedHeaders = [...MASTER_LIST_HEADERS];

      const processedDataRows: Row[] = videos.map((video): Row => {
        const values = videoToRowValues(video);
        const cells: Cell[] = values.map(value => ({ value, isEditing: false }));
        return {
          cells,
          isNew: false,
          videoId: video.id, // DB id, used for update/delete
        };
      });

      const newBlankRow: Row = {
        cells: fetchedHeaders.map(() => ({ value: '', isEditing: false })),
        isNew: true,
      };

      setHeaders(fetchedHeaders);
      setRows([newBlankRow, ...processedDataRows]);

    } catch (error: unknown) { // Type the error
      console.error('[VideoManager] Detailed error in loadData:', error);
      setError(`Failed to load data: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setHeaders([]);
        setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCellEdit = (rowIndex: number, colIndex: number) => {
    // Never allow S3 URL column to be edited
    if (headers[colIndex]?.toLowerCase().includes('s3 url')) {
      console.log('S3 URL cell edit prevented');
      return;
    }
    
    // Skip S3 URL column completely to prevent any editing
    if (colIndex >= 0 && headers[colIndex]?.toLowerCase().includes('s3 url')) {
      return; // Do nothing for S3 URL cells
    }
    console.log(`[DEBUG] handleCellEdit called for row ${rowIndex}, col ${colIndex}. Header: ${headers[colIndex]}`); // Added log

    // Set only the clicked cell to editing mode
    setRows(prevRows => {
      const newRows = [...prevRows];
      // First, set all cells to not editing
      newRows.forEach(row => {
        row.cells.forEach(cell => {
          cell.isEditing = false;
        });
      });
      // Then set the clicked cell to editing
      newRows[rowIndex].cells[colIndex].isEditing = true;
      console.log(`[DEBUG] setRows in handleCellEdit: for row ${rowIndex}, col ${colIndex}, isEditing is now ${newRows[rowIndex].cells[colIndex].isEditing}`); // Added log
      return newRows;
    });
  };

  const handleCellChange = async (rowIndex: number, colIndex: number, value: string) => {
    console.log(`[DEBUG] handleCellChange called for row ${rowIndex}, col ${colIndex}, value: ${value}. Header: ${headers[colIndex]}`); // Added log

    // Update local React state for the cell's value.
    // If it's the YouTube link in the new row, also try to fetch and set song/artist.
    if (rowIndex === 0 && headers[colIndex].toLowerCase().includes('youtube')) {
      try {
        const songInfo = await youtubeService.getSongInfo(value);
        // Find the indices for song and artist columns using `find` to get the header string, then `indexOf`
        const songColHeader = headers.find(h => h.toLowerCase().includes('song'));
        const artistColHeader = headers.find(h => h.toLowerCase().includes('artist'));

        setRows(prevRows => {
          const newRows = [...prevRows];
          // Ensure the cells array for the current row exists
          if (!newRows[rowIndex] || !newRows[rowIndex].cells) {
            console.error(`[VideoManager] New row ${rowIndex} or its cells not found during YouTube link update.`);
            return prevRows; // or handle error appropriately
          }
          const currentRowCells = newRows[rowIndex].cells;

          // Update the YouTube link cell itself
          if (currentRowCells[colIndex]) {
            currentRowCells[colIndex].value = value;
          }

          // Update song and artist if columns exist
          if (songColHeader) {
            const songColIdx = headers.indexOf(songColHeader);
            if (songColIdx !== -1 && currentRowCells[songColIdx]) {
              currentRowCells[songColIdx].value = songInfo.song;
            }
          }
          if (artistColHeader) {
            const artistColIdx = headers.indexOf(artistColHeader);
            if (artistColIdx !== -1 && currentRowCells[artistColIdx]) {
              currentRowCells[artistColIdx].value = songInfo.artist;
            }
          }
          return newRows;
        });
      } catch (error) {
        console.error('Error extracting song info for new row, still updating YouTube URL:', error);
        setRows(prevRows => {
          const newRows = [...prevRows];
          if (newRows[rowIndex] && newRows[rowIndex].cells && newRows[rowIndex].cells[colIndex]) {
            newRows[rowIndex].cells[colIndex].value = value; // Update only the YouTube URL cell
          }
          return newRows;
        });
      }
    } else {
      // Normal cell value update for all other cases (including checkboxes on existing rows)
      setRows(prevRows => {
        const newRows = [...prevRows];
        if (newRows[rowIndex] && newRows[rowIndex].cells && newRows[rowIndex].cells[colIndex]) {
          newRows[rowIndex].cells[colIndex].value = value;
        }
        return newRows;
      });
    }

    // AFTER local state for the value is updated:
    // For existing data rows (not the "new row" at index 0),
    // if the changed cell is a 'zoom' or 'video' (checkbox type),
    // save the change to Google Sheets immediately.
    const columnType = headers[colIndex]?.toLowerCase() || '';
    if (rowIndex !== 0 && (columnType.includes('zoom') || columnType.includes('video'))) {
      console.log(`[DEBUG] Checkbox change for existing row ${rowIndex} (UI index), col ${colIndex} (${headers[colIndex]}). Saving immediately with value: "${value}"`);
      // Pass the new value directly to handleCellSave.
      // handleCellSave will update the sheet and then set isEditing to false.
      await handleCellSave(rowIndex, colIndex, value);
    }
  };

  const handleCellSave = async (rowIndex: number, colIndex: number, valueToSave?: string) => {
    // Ensure we have the latest row data before saving
    const currentRows = [...rows]; // Get a snapshot
    const row = currentRows[rowIndex];
    if (!row) {
       console.error(`[handleCellSave] Row index ${rowIndex} not found.`);
       return;
    }
    const cell = row.cells[colIndex];
    if (!cell) {
        console.error(`[handleCellSave] Cell index ${colIndex} not found for row ${rowIndex}.`);
        return;
    }
    // Use provided valueToSave if available, otherwise use current cell value from state
    const finalValueToSave = typeof valueToSave !== 'undefined' ? valueToSave : cell.value;
    console.log(`[DEBUG] handleCellSave called for row ${rowIndex}, col ${colIndex}. Header: ${headers[colIndex]}. Attempting to save value: "${finalValueToSave}"`); // Added log

    try {
      // Special handling for new rows (index 0) - just update local editing state
      if (row.isNew) {
        setRows(prevRows => {
          const newRows = [...prevRows];
          if (newRows[rowIndex] && newRows[rowIndex].cells[colIndex]) {
             newRows[rowIndex].cells[colIndex].isEditing = false;
          } else {
             console.error(`[handleCellSave] New Row: Row or cell index out of bounds during state update.`);
          }
          return newRows;
        });
        return; // Don't save new row cell individually or fetch info yet
      }

      // --- For existing rows, update the underlying video in the DB ---
      console.log(`[handleCellSave] Saving existing row ${rowIndex}, col ${colIndex}`);
      const videoId = row.videoId;
      const field = headerToVideoField(headers[colIndex] || '');
      if (!videoId) {
        console.error(`[handleCellSave] No videoId for row ${rowIndex}; cannot save.`);
        setError('Failed to save changes');
        return;
      }
      if (!field) {
        console.error(`[handleCellSave] Column "${headers[colIndex]}" is not a writable video field.`);
        return;
      }
      await chartDataService.updateVideo(videoId, { [field]: finalValueToSave ?? '' });
      console.log(`[handleCellSave] Saved video ${videoId}: ${field} = ${finalValueToSave}`);

      // Update local state to mark as not editing
      setRows(prevRows => {
        const newRows = [...prevRows];
         if (newRows[rowIndex] && newRows[rowIndex].cells[colIndex]) {
           newRows[rowIndex].cells[colIndex].isEditing = false;
         } else {
            console.error(`[handleCellSave] Existing Row: Row or cell index out of bounds during state update.`);
         }
        return newRows;
      });

      // --- Check if the YouTube link was updated and fetch info ---
      const isYouTubeLinkColumn = headers[colIndex]?.toLowerCase().includes('youtube link');
      if (isYouTubeLinkColumn && finalValueToSave) {
         console.log(`[handleCellSave] YouTube link saved for row ${rowIndex}, triggering info fetch...`);
         // Use the captured cellValue
         await fetchAndUpdateSongArtist(rowIndex, finalValueToSave);
      }

    } catch (err) {
      console.error(`[handleCellSave] Error saving cell for row ${rowIndex}, col ${colIndex}:`, err);
      setError('Failed to save changes');
    } finally {
      // Optional: Ensure editing is off regardless of success/failure for existing rows
      // if (rowIndex !== 0) { ... }
    }
  };

  const handleNewRowSave = async (rowIndex: number) => {
    if (!rows[rowIndex].isNew) return;

    // Prepare the values to be saved
    const values = rows[rowIndex].cells.map(cell => cell.value);

    try {
      // --- Check for YouTube URL and fetch info BEFORE saving ---
      const youtubeColIndex = headers.findIndex(h => h.toLowerCase().includes('youtube link'));
      const songColIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
      const artistColIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));

      if (youtubeColIndex !== -1 && songColIndex !== -1 && artistColIndex !== -1) {
        const youtubeUrl = values[youtubeColIndex];
        if (youtubeUrl) {
          console.log('[handleNewRowSave] Found YouTube URL, fetching info before saving...');
          try {
            const songInfo = await youtubeService.getSongInfo(youtubeUrl);
            console.log('[handleNewRowSave] Fetched info:', songInfo);
            // Update the values array directly
            values[songColIndex] = songInfo.song;
            values[artistColIndex] = songInfo.artist;
            console.log('[handleNewRowSave] Updated values array with fetched info.');
          } catch (infoError) {
            console.error('[handleNewRowSave] Error fetching song info for new row:', infoError);
            // Proceed with saving even if info fetch fails
          }
        }
      }
      // --- End of info fetch ---

      // Build the typed video payload from the new row's cells, mapping each
      // column to its Video field via the fixed Master List header order.
      const fields: Partial<Omit<Video, 'id'>> = {};
      headers.forEach((header, index) => {
        const field = headerToVideoField(header);
        if (field) {
          fields[field] = (values[index] ?? '').toString();
        }
      });

      if (!fields.song || !fields.artist) {
        setError('Song and Artist are required to add a video.');
        return;
      }

      console.log('[handleNewRowSave] Creating video with fields:', fields);
      await chartDataService.createVideo(fields as Partial<Omit<Video, 'id'>> & { song: string; artist: string });

      // Reload data to reflect the changes (already sorted by song/artist)
      console.log('[handleNewRowSave] New row saved, reloading data...');
      await loadData();
    } catch (err) {
      console.error('[handleNewRowSave] Error saving new row:', err);
      setError('Failed to save new row');
    }
  };

  const handleCellCancel = (rowIndex: number, colIndex: number) => {
    setRows(prevRows => {
      const newRows = [...prevRows];
      newRows[rowIndex].cells[colIndex].isEditing = false;
      return newRows;
    });
  };

  const handleNewRowCancel = (rowIndex: number) => {
    setRows(prevRows => {
      const newRows = [...prevRows];
      // Clear all cell values in the new row
      newRows[rowIndex].cells.forEach(cell => {
        cell.value = '';
        cell.isEditing = false;
      });
      return newRows;
    });
  };

  const handleSort = (columnName: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === columnName) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    }
    setSortConfig({ key: columnName, direction });
    
    // Sort the rows in the frontend without modifying the Google Sheet
    setRows(prevRows => {
      const [blankRow, ...dataRows] = prevRows;
      const columnIndex = headers.findIndex(h => h.toLowerCase().includes(columnName.toLowerCase()));
      
      const sortedDataRows = [...dataRows].sort((a, b) => {
        const valueA = a.cells[columnIndex]?.value?.toLowerCase() || '';
        const valueB = b.cells[columnIndex]?.value?.toLowerCase() || '';
        return direction === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      });
      
      return [blankRow, ...sortedDataRows];
    });
  };

  // NOTE: "Sync from PROD" was removed in the DB migration. The Master Video
  // List is a single `videos` DB table (no separate PROD/DEV spreadsheets), so
  // there is nothing to sync. Prod/dev isolation is handled at the branch level.

  const getColumnStyle = (columnType: string): React.CSSProperties => {
    const name = columnType.toLowerCase();
    const width = getColumnWidth(columnType);

    // Base style with resizable width
    const baseStyle: React.CSSProperties = {
      width: `${width}px`,
      minWidth: '50px',
      maxWidth: '500px',
    };

    if (name.includes('song') || name.includes('artist')) {
      return {
        ...baseStyle,
        whiteSpace: 'nowrap',
      };
    }
    if (name.includes('zoom')) {
      return {
        ...baseStyle,
        textAlign: 'center',
        padding: '0 8px'
      };
    }
    if (name.includes('s3 url')) {
      return {
        ...baseStyle,
        textAlign: 'left',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
      };
    }
    if (name.includes('vertical url')) {
      return {
        ...baseStyle,
        textAlign: 'left'
      };
    }
    if (name.includes('youtube')) {
      return {
        ...baseStyle,
        textAlign: 'left'
      };
    }
    if (name.includes('in point') || name.includes('seek in')) {
      return {
        ...baseStyle,
        textAlign: 'center'
      };
    }
    if (name.includes('duration')) {
      return {
        ...baseStyle,
        textAlign: 'center'
      };
    }
    if (name.includes('peak')) {
      return {
        ...baseStyle,
        textAlign: 'center',
        padding: '0 8px'
      };
    }
    if (name.includes('weeks on')) {
      return {
        ...baseStyle,
        textAlign: 'center',
        padding: '0 8px'
      };
    }
    return {
      ...baseStyle,
      textAlign: 'left'
    };
  };

  // Strip http:// or https:// and www. from URL for display
  const stripProtocol = (url: string): string => {
    return url.replace(/^https?:\/\//, '').replace(/^www\./, '');
  };

  // Format YouTube URL for compact display: "youtube.com/..."
  const formatYoutubeDisplay = (url: string): string => {
    const stripped = stripProtocol(url);
    if (stripped.startsWith('youtube.com/') || stripped.startsWith('youtu.be/')) {
      const domain = stripped.startsWith('youtube.com/') ? 'youtube.com/' : 'youtu.be/';
      return domain + '...';
    }
    return stripped.length > 20 ? `${stripped.substring(0, 20)}...` : stripped;
  };

  // Get display name for column headers
  const getDisplayHeaderName = (header: string): string => {
    const name = header.toLowerCase();
    if (name.includes('s3 url')) return 'Horizontal';
    if (name.includes('vertical url')) return 'Vertical';
    if (name.includes('youtube')) return 'YouTube';
    return header;
  };

  // Get reordered column indices for display (Vertical between Horizontal and YouTube)
  const getColumnDisplayOrder = React.useCallback((): number[] => {
    const order: number[] = [];
    const s3Index = headers.findIndex(h => h.toLowerCase().includes('s3 url'));
    const verticalIndex = headers.findIndex(h => h.toLowerCase().includes('vertical url'));
    const youtubeIndex = headers.findIndex(h => h.toLowerCase().includes('youtube'));

    headers.forEach((_, index) => {
      // Skip these - we'll insert them in custom order
      if (index === s3Index || index === verticalIndex || index === youtubeIndex) return;
      order.push(index);
    });

    // Find where S3 URL would naturally be and insert our custom order there
    if (s3Index !== -1) {
      // Insert: Horizontal (S3), Vertical, YouTube in that order at the S3 position
      let insertPos = order.findIndex(i => i > s3Index);
      if (insertPos === -1) insertPos = order.length;

      const toInsert = [s3Index, verticalIndex, youtubeIndex].filter(i => i !== -1);
      order.splice(insertPos, 0, ...toInsert);
    }

    return order;
  }, [headers]);

  // Get default width for a column (in pixels)
  const getDefaultColumnWidth = (columnName: string): number => {
    const name = columnName.toLowerCase();
    if (name.includes('song') || name.includes('artist')) return 225;
    if (name.includes('zoom')) return 60;
    if (name.includes('s3 url')) return 120;
    if (name.includes('vertical url')) return 120;
    if (name.includes('youtube')) return 130;
    if (name.includes('in point') || name.includes('seek in')) return 70;
    if (name.includes('duration')) return 80;
    if (name.includes('peak')) return 50;
    if (name.includes('weeks on')) return 120;
    return 120;
  };

  // Get the current width for a column (from state or default)
  const getColumnWidth = (columnName: string): number => {
    return columnWidths[columnName] || getDefaultColumnWidth(columnName);
  };

  // Handle resize start
  const handleResizeStart = (e: React.MouseEvent, columnName: string) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingColumn(columnName);
    setResizeStartX(e.clientX);
    setResizeStartWidth(getColumnWidth(columnName));
  };

  // Handle resize move
  const handleResizeMove = React.useCallback((e: MouseEvent) => {
    if (!resizingColumn) return;
    const diff = e.clientX - resizeStartX;
    const minWidth = minColumnWidths[resizingColumn] || 50;
    const newWidth = Math.max(minWidth, Math.min(500, resizeStartWidth + diff));
    setColumnWidths(prev => ({
      ...prev,
      [resizingColumn]: newWidth
    }));
  }, [resizingColumn, resizeStartX, resizeStartWidth, minColumnWidths]);

  // Handle resize end
  const handleResizeEnd = React.useCallback(() => {
    if (resizingColumn) {
      // Save to localStorage
      const newWidths = { ...columnWidths };
      localStorage.setItem(COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(newWidths));
    }
    setResizingColumn(null);
  }, [resizingColumn, columnWidths]);

  // Attach/detach mouse event listeners during resize
  React.useEffect(() => {
    if (resizingColumn) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [resizingColumn, handleResizeMove, handleResizeEnd]);

  // Measure header text widths to set minimum column widths
  React.useEffect(() => {
    if (headers.length === 0) return;

    const newMinWidths: Record<string, number> = {};
    headers.forEach(header => {
      const el = headerRefs.current[header];
      if (el) {
        // Measure the text width using a temporary span
        const span = document.createElement('span');
        span.style.visibility = 'hidden';
        span.style.position = 'absolute';
        span.style.whiteSpace = 'nowrap';
        span.style.font = window.getComputedStyle(el).font;
        span.style.fontWeight = '900';
        span.style.fontSize = '1.25rem';
        span.textContent = header;
        document.body.appendChild(span);
        const textWidth = span.offsetWidth + 32; // Add padding
        document.body.removeChild(span);
        newMinWidths[header] = Math.max(50, textWidth);
      }
    });
    setMinColumnWidths(newMinWidths);
  }, [headers]);

  const handleCellBlur = (rowIndex: number, colIndex: number) => {
    // First, set the cell to not editing
    setRows(prevRows => {
      const newRows = [...prevRows];
      if (newRows[rowIndex] && newRows[rowIndex].cells[colIndex]) { // Add boundary checks
        newRows[rowIndex].cells[colIndex].isEditing = false;
        console.log(`[DEBUG] handleCellBlur: for row ${rowIndex}, col ${colIndex}, isEditing set to false. Header: ${headers[colIndex]}`); // Added log
      } else {
        console.warn(`[DEBUG] handleCellBlur: Could not find cell at row ${rowIndex}, col ${colIndex} to set isEditing to false.`);
      }
      return newRows;
    });

    // For rows other than the first row, save changes to Google Sheets immediately
    if (rowIndex !== 0) {
      console.log(`[DEBUG] handleCellBlur: rowIndex is ${rowIndex}, calling handleCellSave for col ${colIndex}. Header: ${headers[colIndex]}`); // Added log
      handleCellSave(rowIndex, colIndex);
    } else {
      console.log(`[DEBUG] handleCellBlur: rowIndex is 0 (new row), not calling handleCellSave for col ${colIndex}. Header: ${headers[colIndex]}`); // Added log
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, rowIndex: number, colIndex: number) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      
      // Calculate next cell position
      let nextRowIndex = rowIndex;
      let nextColIndex = colIndex + 1;
      
      // Skip the trash can column (index 0)
      if (nextColIndex === 0) {
        nextColIndex = 1; // Start at Song column (index 1)
      }
      
      // If we're at the Peak column (last data column), move to the Song column of the next row
      if (nextColIndex === headers.length) {  // Peak is the last column (headers.length includes trash can)
        nextColIndex = 1; // Start at Song column (index 1)
        nextRowIndex = rowIndex + 1;
      }
      
      // Only proceed if we have a valid next cell
      if (nextRowIndex < rows.length) {
        // First, set the current cell to not editing
        setRows(prevRows => {
          const newRows = [...prevRows];
          newRows[rowIndex].cells[colIndex].isEditing = false;
          return newRows;
        });

        // Then, set the next cell to editing and focus it
        requestAnimationFrame(() => {
          setRows(prevRows => {
            const newRows = [...prevRows];
            newRows[nextRowIndex].cells[nextColIndex].isEditing = true;
            return newRows;
          });

          // Focus the next cell's input
          const nextInput = document.querySelector(`[data-row="${nextRowIndex}"][data-col="${nextColIndex}"] input`);
          if (nextInput) {
            (nextInput as HTMLInputElement).focus();
          }
        });
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (rowIndex === 0) {
        // Cancel all cells in the new row
        setRows(prevRows => {
          const newRows = [...prevRows];
          newRows[rowIndex].cells.forEach(cell => {
            cell.isEditing = false;
            cell.value = '';
          });
          return newRows;
        });
      } else {
        handleCellCancel(rowIndex, colIndex);
      }
    } else if (e.key === 'Enter' || e.key === 'Return') {
      e.preventDefault();
      if (e.metaKey) {
        // Cmd/Ctrl+Enter: save new row or just exit editing
        if (rowIndex === 0) {
          handleNewRowSave(rowIndex);
        } else {
          setRows(prevRows => {
            const newRows = [...prevRows];
            newRows[rowIndex].cells[colIndex].isEditing = false;
            return newRows;
          });
        }
      } else {
        // Simple Enter: save changes and exit editing mode
        setRows(prevRows => {
          const newRows = [...prevRows];
          newRows[rowIndex].cells[colIndex].isEditing = false;
          return newRows;
        });
        
        // For non-new rows, save the changes to Google Sheets
        if (rowIndex !== 0) {
          handleCellSave(rowIndex, colIndex);
        }
      }
    }
  };

  const getPlaceholderText = (columnType: string, rowIndex: number): string => {
    if (rowIndex === 0) {
      if (columnType.includes('song')) {
        return 'Add new song here';
      }
      if (columnType.includes('artist')) {
        return 'Artist';
      }
      if (columnType.includes('link')) {
        return 'URL';
      }
      if (columnType.includes('in point') || columnType.includes('duration')) {
        return '0';
      }
      if (columnType.includes('peak')) {
        return '0';
      }
      if (columnType.includes('zoom')) {
        return '';
      }
      return '';
    }
    if (columnType.includes('song')) {
      return 'Song';
    } else if (columnType.includes('artist')) {
      return 'Artist';
    } else if (columnType.includes('link')) {
      return 'URL';
    } else if (columnType.includes('zoom')) {
      return '';
    } else if (columnType.includes('in point')) {
      return '0';
    } else if (columnType.includes('video')) {
      return 'FALSE';
    } else if (columnType.includes('duration')) {
      return '0';
    } else if (columnType.includes('peak')) {
      return '0';
    } else {
      return '0';
    }
  };

  const handleSearchIconClick = (rowIndex: number) => {
    const songColIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
    const artistColIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));

    if (songColIndex !== -1 && artistColIndex !== -1) {
      const song = rows[rowIndex]?.cells[songColIndex]?.value || '';
      const artist = rows[rowIndex]?.cells[artistColIndex]?.value || '';
      const query = `${song} ${artist}`.trim();
      setSearchInitialQuery(query);
    } else {
      setSearchInitialQuery('');
    }
    setSearchTargetRowIndex(rowIndex);
    setSearchDialogOpen(true);
  };

  const handleSearchDialogClose = () => {
    setSearchDialogOpen(false);
    setSearchInitialQuery('');
    setSearchTargetRowIndex(null);
  };

  // Function to fetch song/artist info and update the row
  const fetchAndUpdateSongArtist = async (rowIndex: number, youtubeUrl: string) => {
    if (!youtubeUrl || rowIndex === null) return;

    console.log(`[VideoManager] Fetching song/artist for row ${rowIndex}, URL: ${youtubeUrl}`);
    try {
      const songInfo = await youtubeService.getSongInfo(youtubeUrl);
      console.log(`[VideoManager] Fetched info for row ${rowIndex}:`, songInfo);

      const songColIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
      const artistColIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));

      if (songColIndex === -1 || artistColIndex === -1) {
        console.error('[VideoManager] Could not find Song or Artist columns.');
        return;
      }

      // Update local state first for immediate UI feedback
      setRows(prevRows => {
        const newRows = [...prevRows];
        if (newRows[rowIndex]) {
          if (newRows[rowIndex].cells[songColIndex]) {
             newRows[rowIndex].cells[songColIndex].value = songInfo.song;
          }
          if (newRows[rowIndex].cells[artistColIndex]) {
             newRows[rowIndex].cells[artistColIndex].value = songInfo.artist;
          }
          console.log(`[VideoManager] Updated local state for row ${rowIndex} with song/artist.`);
        } else {
            console.error(`[VideoManager] Row index ${rowIndex} not found in state.`);
        }
        return newRows;
      });

      // Persist changes to Google Sheet if it's an existing row
      if (rowIndex !== 0) {
        console.log(`[VideoManager] Saving fetched song/artist for existing row ${rowIndex}...`);
        await handleCellSave(rowIndex, songColIndex); // Save song
        await handleCellSave(rowIndex, artistColIndex); // Save artist
        console.log(`[VideoManager] Saved fetched song/artist for row ${rowIndex}.`);
      }

    } catch (error) {
      console.error(`[VideoManager] Error fetching or updating song/artist info for row ${rowIndex}:`, error);
      // Optional: Update UI to show an error specific to this action
    }
  };

  const handleSearchDialogSelect = async (videoId: string) => {
    console.log('[VideoManager] handleSearchDialogSelect received videoId:', videoId);
    if (searchTargetRowIndex !== null) {
      const youtubeColIndex = headers.findIndex(h => h.toLowerCase().includes('youtube link'));
      if (youtubeColIndex !== -1) {
        const fullUrl = `https://www.youtube.com/watch?v=${videoId}`;
        console.log('[VideoManager] Constructed URL:', fullUrl);
        console.log('[VideoManager] Target row index:', searchTargetRowIndex);
        console.log('[VideoManager] Target column index:', youtubeColIndex);

        // --- Update local state for the URL ---
        setRows(prevRows => {
          console.log('[VideoManager] Updating rows state with URL...');
          const newRows = [...prevRows];
          if (newRows[searchTargetRowIndex] && newRows[searchTargetRowIndex].cells[youtubeColIndex]) {
            newRows[searchTargetRowIndex].cells[youtubeColIndex].value = fullUrl;
            if (searchTargetRowIndex !== 0) {
               newRows[searchTargetRowIndex].cells[youtubeColIndex].isEditing = false;
            }
            console.log('[VideoManager] URL State update applied:', newRows[searchTargetRowIndex].cells[youtubeColIndex]);
          } else {
            console.error('[VideoManager] Error: Target row or cell not found for URL state update.');
          }
          return newRows;
        });

        // --- Persist URL if it's an existing row ---
        if (searchTargetRowIndex !== 0) {
          console.log('[VideoManager] Calling handleCellSave for URL on existing row...');
          await handleCellSave(searchTargetRowIndex, youtubeColIndex);
          console.log('[VideoManager] handleCellSave for URL completed.');
        }

        // --- Fetch and update Song/Artist info ---
        console.log('[VideoManager] Triggering fetchAndUpdateSongArtist...');
        await fetchAndUpdateSongArtist(searchTargetRowIndex, fullUrl);

      } else {
         console.error('[VideoManager] YouTube Link column not found.');
      }
    } else {
       console.error('[VideoManager] searchTargetRowIndex is null.');
    }
    handleSearchDialogClose();
  };

  const handleDeleteClick = (rowIndex: number) => {
    console.log('=== Delete Button Clicked ===');
    console.log('Local row index clicked:', rowIndex);
    console.log('Row data:', rows[rowIndex]);
    console.log('Total rows in local state:', rows.length);
    console.log('Headers:', headers);
    setRowToDelete(rowIndex);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (rowToDelete === null) return;

    try {
      console.log('=== Delete Confirmation Started ===');
      console.log('Local row index to delete:', rowToDelete);
      console.log('Row data to delete:', rows[rowToDelete]);
      
      // Don't allow deleting the blank row (index 0)
      if (rowToDelete === 0) {
        console.log('Attempted to delete blank new row:', rowToDelete);
        setDeleteDialogOpen(false);
        setRowToDelete(null);
        return;
      }

      // Get the DB id of the underlying video row
      const videoId = rows[rowToDelete].videoId;
      if (!videoId) {
        console.error('No videoId found for row:', rowToDelete);
        setDeleteDialogOpen(false);
        setRowToDelete(null);
        return;
      }
      console.log('Deleting video id:', videoId);

      // Delete the video from the DB
      await chartDataService.deleteVideo(videoId);
      console.log('Delete request completed');

      console.log('Reloading data to reflect deletion...');
      await loadData();
      console.log('Data reload completed');
      
      setDeleteDialogOpen(false);
      setRowToDelete(null);
      console.log('=== Delete Operation Completed ===');
    } catch (err) {
      console.error('Error in delete operation:', err);
      setError('Failed to delete row');
    }
  };

  const handleSendJson = async () => {
    // The legacy API-request preview dialog (Google Sheets batchUpdate) is no
    // longer used now that the Master Video List is a typed DB table. Deletions
    // happen via handleDeleteConfirm -> chartDataService.deleteVideo. This just
    // closes the (never-opened) preview dialog.
    setPreviewDialogOpen(false);
  };

  const handleInPointClick = (rowIndex: number, colIndex: number) => {
    // Find the S3 URL column index
    const s3UrlColIndex = headers.findIndex(h => h.toLowerCase().includes('s3 url'));
    if (s3UrlColIndex === -1) {
      console.error('[VideoManager] S3 URL column not found');
      setError('S3 URL column is missing.');
      return;
    }

    const s3Url = rows[rowIndex].cells[s3UrlColIndex].value;
    if (!s3Url) {
      console.error('[VideoManager] No S3 URL found for row:', rowIndex);
      setError('No S3 URL set for this video.');
      return;
    }

    // Find the in point and duration column indices
    const inPointColIndex = headers.findIndex(h => h.toLowerCase().includes('in point'));
    const durationColIndex = headers.findIndex(h => h.toLowerCase().includes('duration'));
    if (inPointColIndex === -1 || durationColIndex === -1) {
      console.error('[VideoManager] In Point or Duration column not found');
      setError('In Point or Duration column is missing.');
      return;
    }

    const inPointValue = rows[rowIndex].cells[inPointColIndex].value || '0';
    const durationValue = rows[rowIndex].cells[durationColIndex].value || '0'; // Get duration value

    console.log(`[VideoManager] handleInPointClick - Row: ${rowIndex}, S3 URL: ${s3Url}, In Point: ${inPointValue}, Duration: ${durationValue}`);

    // Set state to open the player
    setCurrentRowIndex(rowIndex);
    setCurrentVideoUrl(s3Url);
    setCurrentInPoint(inPointValue);
    setCurrentDuration(durationValue); // Set duration state

    setTimeout(() => {
       console.log('[VideoManager] Opening video player from handleInPointClick');
       setVideoPlayerDialogOpen(true);
    }, 100);
  };

  const handleVideoPlayerClose = () => {
    console.log('[VideoManager] Closing video player dialog.');
    setVideoPlayerDialogOpen(false);
    setCurrentVideoUrl('');
    setCurrentRowIndex(-1);
    setCurrentInPoint('0');
    setCurrentDuration('0'); // Reset duration state
    setIsPreviewing(false);
    
    // Instead of relying on URL params which might be cleared, use our state
    if (openedFromCharts) {
      console.log('[VideoManager] Returning to charts page after closing video opened from charts');
      navigate('/');
      // Reset the state
      setOpenedFromCharts(false);
    } else {
      console.log('[VideoManager] Not returning to charts (openedFromCharts=false)');
    }
  };

  const handlePreview = () => {
    if (currentRowIndex === -1 || isPreviewing) return;

    const videoElement = document.getElementById('video-player') as HTMLVideoElement | null;
    if (!videoElement) return;

    // Use local state for preview start point
    const inPoint = parseFloat(currentInPoint);
    let duration = parseFloat(currentDuration); // Use local state for duration

    if (isNaN(inPoint)) {
      console.error('Invalid In Point value for preview.');
      setError('Invalid In Point value for preview.');
      return;
    }
    if (isNaN(duration) || duration <= 0) {
        duration = 5;
        console.warn(`Invalid or zero duration, defaulting to 5s preview.`);
    }

    console.log(`[VideoManager] Previewing from ${inPoint}s for ${duration}s`);
    setIsPreviewing(true);
    videoElement.currentTime = inPoint;
    videoElement.play();

    // Clear existing timeout if any
    if (previewTimeout) clearTimeout(previewTimeout);

    // Set timeout to pause after duration
    const timeout = setTimeout(() => {
      if (videoElement) {
        videoElement.pause();
      }
      setIsPreviewing(false);
      setPreviewTimeout(null);
      console.log('[VideoManager] Preview finished.');
    }, duration * 1000);
    setPreviewTimeout(timeout);
  };

  // Ref to track if we've already initialized to prevent duplicate loads
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    const initializeApp = async () => {
      // Prevent duplicate initialization
      if (hasInitializedRef.current) {
        console.log('[VideoManager] Already initialized, skipping...');
        return;
      }
      hasInitializedRef.current = true;

      console.log('[VideoManager] Initializing Master Video List from DB');

      try {
        setIsInitialized(true);
        await loadData();
      } catch (err) {
        console.error('Error initializing app:', err);
        setError('Error initializing app. Please refresh the page and try again.');
        hasInitializedRef.current = false; // Allow retry on error
      } finally {
        setLoading(false);
      }
    };

    initializeApp();

    // Check if we need to automatically open the player with a specific video
    const params = new URLSearchParams(location.search);
    const hasPlayerParam = params.get('player') === 'true';
    const songParam = params.get('song');
    const artistParam = params.get('artist');
    const s3UrlParam = params.get('s3url');

    console.log(`[VideoManager] URL params: player=${hasPlayerParam}, song=${songParam}, artist=${artistParam}, s3url=${s3UrlParam}`);

    // Save whether we were opened from charts
    if (hasPlayerParam) {
      console.log('[VideoManager] Setting openedFromCharts=true');
      setOpenedFromCharts(true);
    }

    // If s3url is provided directly, use it
    if (hasPlayerParam && s3UrlParam) {
      console.log(`[VideoManager] Opening video player with provided S3 URL: ${s3UrlParam}`);
      setCurrentVideoUrl(s3UrlParam);
      setCurrentInPoint('0'); // Default In Point
      setCurrentDuration('0'); // Default Duration
      setCurrentRowIndex(-1);
      setVideoPlayerDialogOpen(true);
    }
    // Otherwise if we have song/artist, wait for data to load and find the matching row
    else if (hasPlayerParam && songParam && artistParam) {
      // This will be handled by another useEffect after data loads
      console.log(`[VideoManager] Will search for video with song="${songParam}", artist="${artistParam}"`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add a separate effect to handle finding and opening videos by song/artist after data loads
  useEffect(() => {
    // Only run when data has finished loading and we have the player param with song/artist
    if (!loading && isInitialized && rows.length > 0 && headers.length > 0) {
      const params = new URLSearchParams(location.search);
      const hasPlayerParam = params.get('player') === 'true';
      const songParam = params.get('song');
      const artistParam = params.get('artist');
      const s3UrlParam = params.get('s3url');
      
      // Skip if we don't have the necessary params
      if (!hasPlayerParam) return;
      
      // Immediately clear URL parameters to prevent duplicate processing
      console.log('[VideoManager] Clearing URL parameters immediately to prevent duplicates');
      navigate('/video-manager', { replace: true });
      
      // If S3 URL is directly provided, use it directly without searching for the row
      if (s3UrlParam) {
        console.log(`[VideoManager] Using provided S3 URL directly: ${s3UrlParam}`);
        setCurrentVideoUrl(s3UrlParam);
        setCurrentInPoint('0'); // Default In Point
        setCurrentDuration('0'); // Default Duration
        setCurrentRowIndex(-1);
        
        // If we also have song/artist, try to find the matching row for In Point
        if (songParam && artistParam) {
          console.log(`[VideoManager] Looking for matching row to get In Point, song="${songParam}", artist="${artistParam}"`);
          
          // Find the column indices
          const songColIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
          const artistColIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));
          const s3UrlColIndex = headers.findIndex(h => h.toLowerCase().includes('s3 url'));
          const inPointColIndex = headers.findIndex(h => h.toLowerCase().includes('in point'));
          const seekInColIndex = headers.findIndex(h => h.toLowerCase().includes('seek in'));
          const durationColIndex = headers.findIndex(h => h.toLowerCase().includes('duration'));
          
          if (songColIndex !== -1 && artistColIndex !== -1 && s3UrlColIndex !== -1) {
            // Find matching row
            const matchingRowIndex = rows.findIndex(row => {
              if (!row.cells[songColIndex] || !row.cells[artistColIndex]) return false;
              
              const rowSong = row.cells[songColIndex].value?.toLowerCase().trim() || '';
              const rowArtist = row.cells[artistColIndex].value?.toLowerCase().trim() || '';
              const paramSong = songParam.toLowerCase().trim();
              const paramArtist = artistParam.toLowerCase().trim();
              
              return rowSong === paramSong && rowArtist === paramArtist;
            });
            
            if (matchingRowIndex !== -1) {
              console.log(`[VideoManager] Found matching row at index ${matchingRowIndex}`);
              setHighlightedRow(matchingRowIndex);

              const s3Url = rows[matchingRowIndex].cells[s3UrlColIndex]?.value || '';
              console.log(`[VideoManager] S3 URL found: "${s3Url}"`);

              if (s3Url) {
                console.log('[VideoManager] Setting current row and S3 video URL');
                const inPointValue = inPointColIndex !== -1 ? rows[matchingRowIndex].cells[inPointColIndex]?.value || '0' : '0';
                const seekInValue = seekInColIndex !== -1 ? rows[matchingRowIndex].cells[seekInColIndex]?.value || '0' : '0';
                const durationValue = durationColIndex !== -1 ? rows[matchingRowIndex].cells[durationColIndex]?.value || '0' : '0';

                setTimeout(() => {
                  setCurrentRowIndex(matchingRowIndex);
                  setCurrentVideoUrl(s3Url);
                  setCurrentInPoint(inPointValue);
                  setCurrentSeekIn(seekInValue);
                  setCurrentDuration(durationValue);

                  setTimeout(() => {
                    console.log('[VideoManager] Opening video player dialog NOW with S3 URL:', s3Url);
                    setVideoPlayerDialogOpen(true);
                    setTimeout(() => {
                      console.log('[VideoManager] Clearing URL parameters (found S3 URL)');
                      navigate('/video-manager', { replace: true });
                    }, 200);
                  }, 100);
                }, 100);
              }
            }
          }
        }
        
        // Set state *before* opening dialog
        console.log(`[VideoManager] Data set for direct S3 URL, opening player now.`);
        // Directly open dialog with no delay
        setVideoPlayerDialogOpen(true);

        return; // Important: return early to avoid the row search logic below
      }
      
      // If we don't have S3 URL param but have song/artist, try to find the matching row and S3 URL
      if (songParam && artistParam) {
        console.log(`[VideoManager] Data loaded, searching for song="${songParam}", artist="${artistParam}"`);
        
        // Find the column indices
        const songColIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
        const artistColIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));
        const s3UrlColIndex = headers.findIndex(h => h.toLowerCase().includes('s3 url'));
        const inPointColIndex = headers.findIndex(h => h.toLowerCase().includes('in point'));
        const seekInColIndex = headers.findIndex(h => h.toLowerCase().includes('seek in'));
        const durationColIndex = headers.findIndex(h => h.toLowerCase().includes('duration')); // Add duration index

        if (songColIndex === -1 || artistColIndex === -1 || s3UrlColIndex === -1) {
          console.error(`[VideoManager] Could not find required columns. Indices: song=${songColIndex}, artist=${artistColIndex}, s3=${s3UrlColIndex}`);
          return;
        }
        
        // Find the matching row
        const matchingRowIndex = rows.findIndex(row => {
          if (!row.cells[songColIndex] || !row.cells[artistColIndex]) return false;
          
          const rowSong = row.cells[songColIndex].value?.toLowerCase().trim() || '';
          const rowArtist = row.cells[artistColIndex].value?.toLowerCase().trim() || '';
          const paramSong = songParam.toLowerCase().trim();
          const paramArtist = artistParam.toLowerCase().trim();
          
          return rowSong === paramSong && rowArtist === paramArtist;
        });
        
        if (matchingRowIndex !== -1) {
          // IMPORTANT: Use the S3 URL column, not the YouTube Link column
          const s3Url = rows[matchingRowIndex].cells[s3UrlColIndex].value;
          const inPoint = inPointColIndex !== -1 ? rows[matchingRowIndex].cells[inPointColIndex].value || '0' : '0';
          const seekInValue = seekInColIndex !== -1 ? rows[matchingRowIndex].cells[seekInColIndex]?.value || '0' : '0';
          const durationValue = durationColIndex !== -1 ? rows[matchingRowIndex].cells[durationColIndex].value || '0' : '0'; // Get duration value using durationColIndex

          if (s3Url) {
            console.log(`[VideoManager] Found matching row ${matchingRowIndex} with S3 URL: ${s3Url}`);
            setCurrentVideoUrl(s3Url);
            setCurrentInPoint(inPoint);
            setCurrentSeekIn(seekInValue);
            setCurrentDuration(durationValue); // Set duration using the correct value
            setCurrentRowIndex(matchingRowIndex);
            // Set state and immediately open dialog with no delay
            console.log(`[VideoManager] Data set for song/artist, opening player now.`);
            setVideoPlayerDialogOpen(true);
          } else {
            console.warn(`[VideoManager] Found matching row ${matchingRowIndex} but no S3 URL is available`);
          }
        } else {
          console.warn(`[VideoManager] No matching row found for song="${songParam}", artist="${artistParam}"`);
        }
      }
    }
  }, [loading, isInitialized, rows, headers, navigate, location.search]); // Added location.search to dependency array

  // --- S3 URL Save Handler ---
  const handleS3UrlSave = async (newUrl: string) => {
    if (!editingS3UrlState) return;
    const { row, col } = editingS3UrlState;
    console.log(`[handleS3UrlSave] Saving Row: ${row}, Col: ${col}, New URL: ${newUrl}`);
    await handleCellChange(row, col, newUrl); // Update local state first
    await handleCellSave(row, col); // Persist change
    setEditingS3UrlState(null); // Close dialog
  };

  // --- Vertical URL Save Handler ---
  const handleVerticalUrlSave = async (newUrl: string) => {
    if (!editingVerticalUrlState) return;
    const { row, col } = editingVerticalUrlState;
    console.log(`[handleVerticalUrlSave] Saving Row: ${row}, Col: ${col}, New URL: ${newUrl}`);
    await handleCellChange(row, col, newUrl); // Update local state first
    await handleCellSave(row, col); // Persist change
    setEditingVerticalUrlState(null); // Close dialog
    setVerticalVideoSessionId(''); // Clear session ID when dialog closes
  };

  // --- Handle Edit Framing ---
  const handleEditFraming = (verticalUrl: string, inPoint: string, duration: string) => {
    const inPointNum = parseFloat(inPoint) || 0;
    const durationNum = parseFloat(duration) || 30; // Default 30 seconds if no duration
    
    // Get the horizontal S3 URL from the current row
    const s3UrlColIndex = headers.findIndex(h => h.toLowerCase().includes('s3 url'));
    const horizontalUrl = editingVerticalUrlState && s3UrlColIndex !== -1 
      ? rows[editingVerticalUrlState.row]?.cells[s3UrlColIndex]?.value 
      : null;
    
    setFramingEditorData({
      videoUrl: verticalUrl,  // Vertical URL for thumbnails and preview
      horizontalUrl: horizontalUrl || verticalUrl,  // Horizontal URL for editing
      inPoint: inPointNum,
      duration: durationNum
    });
    setFramingEditorOpen(true);
  };

  const handleFramingEditorClose = () => {
    setFramingEditorOpen(false);
    setFramingEditorData(null);
  };

  const handleFramingVideoGenerated = (newVideoUrl: string) => {
    // If we have the current editing state, update the vertical URL
    if (editingVerticalUrlState) {
      setEditingVerticalUrlState(prev => prev ? { ...prev, url: newVideoUrl } : null);
    }

    // Show success message
    enqueueSnackbar('Reframed video generated successfully!', { variant: 'success' });

    // Close the framing editor
    handleFramingEditorClose();
  };

  // --- Handle Generate Vertical ---
  // Navigate to UploadVideo with vertical-only mode for consistent UX
  const handleGenerateVertical = () => {
    if (!editingVerticalUrlState) return;

    const { row, song, artist } = editingVerticalUrlState;

    const s3UrlColIndex = headers.findIndex(h => h.toLowerCase().includes('s3 url'));
    const s3Url = s3UrlColIndex !== -1 ? rows[row]?.cells[s3UrlColIndex]?.value : null;

    if (!s3Url) {
      enqueueSnackbar('No horizontal S3 URL found for this video', { variant: 'error' });
      return;
    }

    // Close the dialog
    setEditingVerticalUrlState(null);

    // Navigate to UploadVideo with vertical generation mode
    const params = new URLSearchParams({
      verticalMode: 'true',
      horizontalS3Url: s3Url,
      videoManagerRowIndex: row.toString(),
      song: song || '',
      artist: artist || '',
    });

    navigate(`/upload-video?${params.toString()}`);
  };

  // --- Handle Seek In Click ---
  const handleSeekInClick = (rowIndex: number, colIndex: number) => {
    // Get the S3 URL for this row
    const s3UrlColIndex = headers.findIndex(h => h.toLowerCase().includes('s3 url'));
    if (s3UrlColIndex === -1) {
      enqueueSnackbar('S3 URL column not found', { variant: 'error' });
      return;
    }

    const s3Url = rows[rowIndex].cells[s3UrlColIndex]?.value;
    if (!s3Url) {
      enqueueSnackbar('No S3 URL found for this row', { variant: 'error' });
      return;
    }

    // Set up the dialog state
    setSeekTimeDialogVideoUrl(s3Url);
    setSeekTimeDialogRowIndex(rowIndex);
    setSeekTimeDialogOpen(true);
  };

  // --- Seek Time Dialog Handler ---
  const handleSeekTimeDialogClose = async (seekTime: number) => {
    if (seekTimeDialogRowIndex < 0) return;
    
    console.log(`[handleSeekTimeDialogClose] Saving seek time ${seekTime} for row ${seekTimeDialogRowIndex}`);
    
    // Find the Seek In column
    const seekInColIndex = headers.findIndex(h => h.toLowerCase().includes('seek in'));
    
    if (seekInColIndex !== -1) {
      try {
        // Update local state and save to sheet
        await handleCellChange(seekTimeDialogRowIndex, seekInColIndex, seekTime.toString());
        await handleCellSave(seekTimeDialogRowIndex, seekInColIndex, seekTime.toString());
        
        console.log(`[handleSeekTimeDialogClose] Successfully saved seek time ${seekTime} to column ${seekInColIndex}`);
        enqueueSnackbar(`Seek time set to ${seekTime} seconds`, { variant: 'success' });
      } catch (error) {
        console.error('[handleSeekTimeDialogClose] Error saving seek time:', error);
        enqueueSnackbar('Failed to save seek time', { variant: 'error' });
      }
    } else {
      console.error('[handleSeekTimeDialogClose] Seek In column not found');
      enqueueSnackbar('Seek In column not found in sheet', { variant: 'error' });
    }
    
    // Close dialog and reset state
    setSeekTimeDialogOpen(false);
    setSeekTimeDialogVideoUrl('');
    setSeekTimeDialogRowIndex(-1);
  };

  // --- Handle Vertical Video Upload ---
  const handleVerticalVideoUpload = async () => {
    if (!verticalVideoUploadFile || !editingVerticalUrlState) {
      console.error('[handleVerticalVideoUpload] No file or editing state available');
      return;
    }

    const { row, song, artist } = editingVerticalUrlState;
    if (!song || !artist) {
      console.error('[handleVerticalVideoUpload] Missing song or artist information');
      setError('Missing song or artist information for the file name');
      return;
    }

    // Format the filename: "(Artist) - (Song) VERTICAL.mp4"
    const fileName = `${artist} - ${song} VERTICAL.mp4`;
    console.log(`[handleVerticalVideoUpload] Uploading as: ${fileName}`);

    try {
      // Create FormData to send the file to the server
      const formData = new FormData();
      formData.append('video', verticalVideoUploadFile);
      formData.append('destination', 'Billboard/Videos');
      formData.append('fileName', fileName);

      // Upload the file with progress tracking using XMLHttpRequest
      const uploadResult = await new Promise<any>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        xhr.open('POST', '/api/upload-s3-video', true);
        
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            setVerticalUploadProgress(percentComplete);
          }
        };
        
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              resolve(response);
            } catch (e) {
              reject(new Error('Invalid response format'));
            }
          } else {
            reject(new Error(`Upload failed with status: ${xhr.status}`));
          }
        };
        
        xhr.onerror = () => {
          reject(new Error('Network error during upload'));
        };
        
        xhr.send(formData);
      });

      console.log('[handleVerticalVideoUpload] Upload success:', uploadResult);
      
      // Update the vertical URL cell with the new S3 URL
      const s3Url = `https://c1-scott-scratchdisk.s3.amazonaws.com/Billboard/Videos/${encodeURIComponent(fileName)}`;
      await handleVerticalUrlSave(s3Url);
      
      // Reset upload state
      setVerticalVideoUploadFile(null);
      setVerticalUploadProgress(0);
    } catch (error) {
      console.error('[handleVerticalVideoUpload] Error:', error);
      setError('Failed to upload vertical video: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  // --- Handle S3 Video Upload ---
  const handleS3VideoUpload = async () => {
    // Upload the file to backend, then navigate to UploadVideo for processing
    if (!s3VideoUploadFile || !editingS3UrlState) {
      console.error('[handleS3VideoUpload] No file selected or editingS3UrlState is not set.');
      setError('No file selected or context is missing for upload.');
      return;
    }

    const { row, song, artist } = editingS3UrlState;
    if (!song || !artist) {
      console.error('[handleS3VideoUpload] Missing song or artist information for filename.');
      setError('Song and Artist information are required for processing.');
      return;
    }

    try {
      // Upload the file to the backend /uploads directory first
      console.log(`[handleS3VideoUpload] Uploading file to backend for processing: ${s3VideoUploadFile.name}`);
      const formData = new FormData();
      formData.append('video', s3VideoUploadFile);

      const uploadResponse = await fetch(`${SERVER_BASE_URL}/api/upload-local-video`, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.error || 'Failed to upload video file to backend');
      }

      const { localVideoPath } = await uploadResponse.json();
      console.log(`[handleS3VideoUpload] File uploaded to backend: ${localVideoPath}`);

      // Navigate to UploadVideo with the local file path (instead of YouTube URL)
      // Subtract 1 from row because rows[0] is the "new blank row" in the UI
      // The server expects a 0-based data index, not the UI row index
      const dataRowIndex = row - 1;
      const params = new URLSearchParams({
        localVideoPath: localVideoPath,
        videoManagerRowIndex: dataRowIndex.toString()
      });

      // Add song and artist
      params.append('song', song);
      params.append('artist', artist);

      console.log(`[handleS3VideoUpload] Navigating to UploadVideo for processing`);
      navigate(`/upload-video?${params.toString()}`);
      setEditingS3UrlState(null); // Close the dialog
      setS3VideoUploadFile(null); // Clear the file
    } catch (error: any) {
      console.error('[handleS3VideoUpload] Error:', error);
      setError(error.message || 'Failed to upload video file');
    }
  };

  // Auto-trigger upload when file is selected
  useEffect(() => {
    if (s3VideoUploadFile && editingS3UrlState) {
      console.log('[VideoManager] File selected, auto-starting upload process');
      handleS3VideoUpload();
    }
  }, [s3VideoUploadFile]);

  // Actual S3 Upload logic (called after filename confirmation)
  const executeS3Upload = async (confirmedFileName: string) => {
    if (!pendingUploadFile || !uploadTargetRowInfo) {
      setError("No file or target row info for upload.");
      return;
    }
    setError(null);
    setS3UploadProgress(0);
    setIsUploadingS3(true); // Indicate upload has started
    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('video', pendingUploadFile);
    formData.append('fileName', confirmedFileName);

    try {
      // This fetch starts the upload and gets the uploadId
      const response = await fetch(`${SERVER_BASE_URL}/api/upload-s3-video`, {
        method: 'POST',
        body: formData,
      });

      if (response.status !== 202) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Server did not start upload process.');
      }

      const { uploadId, s3Url: initialS3Url } = await response.json();
      setCurrentUploadId(uploadId);
      console.log('[executeS3Upload] Upload started with ID:', uploadId);

      // Wait for the upload to complete using EventSource
      const eventSource = await authedEventSource(`${SERVER_BASE_URL}/api/upload-progress/${uploadId}`);
      const uploadResult = await new Promise<any>((resolve, reject) => {

        eventSource.onmessage = (event) => {
          const data = JSON.parse(event.data);
          setS3UploadProgress(data.percentage);
          setUploadProgress(data.percentage);

          if (data.status === 'completed') {
            eventSource.close();
            setIsUploading(false);
            setIsUploadingS3(false);
            console.log('[executeS3Upload] Upload finished successfully!');
            resolve({ success: true, s3Url: data.s3Url || initialS3Url });
          } else if (data.status === 'error') {
            eventSource.close();
            setIsUploading(false);
            setIsUploadingS3(false);
            reject(new Error(data.message || 'Upload failed'));
          }
        };

        eventSource.onerror = (err) => {
          console.error('EventSource failed:', err);
          eventSource.close();
          setIsUploading(false);
          setIsUploadingS3(false);
          reject(new Error('Connection to upload progress stream failed'));
        };
      });

      console.log('[executeS3Upload] Upload success:', uploadResult);
      
      // Use the S3 URL from the upload result
      const finalS3Url = uploadResult.s3Url || `https://c1-scott-scratchdisk.s3.amazonaws.com/Billboard/Videos/${encodeURIComponent(confirmedFileName)}`;
      
      await handleCellChange(uploadTargetRowInfo.rowIndex, headers.findIndex(h => h.toLowerCase().includes('s3 url')), finalS3Url);
      await handleCellSave(uploadTargetRowInfo.rowIndex, headers.findIndex(h => h.toLowerCase().includes('s3 url')), finalS3Url);
      
      // Open seek time dialog after S3 URL is saved
      setSeekTimeDialogVideoUrl(finalS3Url);
      setSeekTimeDialogRowIndex(uploadTargetRowInfo.rowIndex);
      setSeekTimeDialogOpen(true);
      
      setPendingUploadFile(null);
      setCurrentUploadFileName('');
      setS3UploadProgress(0);
      setIsFileNameConfirmationDialogOpen(false);
      setUploadTargetRowInfo(null);
      setEditingS3UrlState(null); // Close the UrlDialog as well

    } catch (error) {
      console.error('[executeS3Upload] Error:', error);
      setError(`Failed to upload S3 video: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setS3UploadProgress(0); // Reset progress on error
      setUploadProgress(0);
      setIsUploading(false);
      // Don't close the dialog on error, let user try again or cancel
    } finally {
      setIsUploadingS3(false); // Indicate upload has finished
      setS3UploadProgress(0); // Reset progress
      setUploadProgress(0);
      setIsUploading(false);
    }
  };

  // Function to render the filename confirmation dialog
  const renderFileNameConfirmationDialog = () => {
    if (!uploadTargetRowInfo) return null; // Don't render if no target
  
    return (
      <Dialog
        open={isFileNameConfirmationDialogOpen}
        onClose={() => {
          setIsFileNameConfirmationDialogOpen(false);
          setPendingUploadFile(null);
          setCurrentUploadFileName('');
          setUploadTargetRowInfo(null);
          setS3UploadProgress(0); // Reset progress if dialog is cancelled
          setError(null); // Clear error on cancel
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm File Name for S3 Upload</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Please confirm or edit the filename for the S3 upload.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="File Name"
            type="text"
            fullWidth
            variant="outlined"
            value={currentUploadFileName}
            onChange={(e) => setCurrentUploadFileName(e.target.value)}
            helperText="The video will be uploaded to Billboard/Videos/ on S3 with this name."
          />
          {s3UploadProgress > 0 && s3UploadProgress < 100 && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <LinearProgress variant="determinate" value={s3UploadProgress} />
              <Typography variant="body2" sx={{ mt: 0.5, textAlign: 'center' }}>
                Uploading: {s3UploadProgress}%
              </Typography>
            </Box>
          )}
          {error && !(s3UploadProgress > 0 && s3UploadProgress < 100) && ( // Show error only if not actively uploading
              <Alert severity="error" sx={{mt: 2}}>{error}</Alert>
          )}
          <DialogContentText sx={{ mt: 1, mb: 2 }}>
            Current S3 Key (if any): {uploadTargetRowInfo?.s3Key || 'N/A'}
          </DialogContentText>
          {isUploadingS3 && (
            <Box sx={{ width: '100%', mt: 2, mb:1 }}>
              <LinearProgress variant="determinate" value={s3UploadProgress} />
              <Typography variant="caption" display="block" gutterBottom align="center">
                {s3UploadProgress > 0 ? `Uploading: ${s3UploadProgress}%` : 'Preparing upload...'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ pb: 2, px:3 }}>
          <Button onClick={() => {
            setIsFileNameConfirmationDialogOpen(false);
            setPendingUploadFile(null);
            setCurrentUploadFileName('');
            setUploadTargetRowInfo(null);
            setS3UploadProgress(0);
            setError(null);
            setIsUploadingS3(false); // Ensure this is reset on cancel
          }} color="secondary" disabled={isUploadingS3}>
            Cancel
          </Button>
          <Button 
            onClick={() => executeS3Upload(currentUploadFileName)} 
            color="primary" 
            variant="contained"
            disabled={isUploadingS3 || !currentUploadFileName.trim()} // Disable if uploading or filename is empty
          >
            {isUploadingS3 ? 'Uploading...' : 'Confirm & Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderCell = (cell: Cell, rowIndex: number, colIndex: number) => {
    const columnType = headers[colIndex]?.toLowerCase() || '';
    const isEmpty = !cell.value;

    // --- Logic for "Add New" Row (rowIndex === 0) ---
    if (rowIndex === 0) {
      if (cell.isEditing) {
        // Editing state for row 0
        if (columnType.includes('zoom')) {
          return (
            <Checkbox 
              checked={cell.value === 'TRUE'} 
              onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.checked ? 'TRUE' : 'FALSE')}
              onBlur={() => handleCellBlur(rowIndex, colIndex)}
              sx={{ 
                '& .MuiSvgIcon-root': {
                  fontSize: '1.25rem',
                  color: 'rgba(0, 0, 0, 0.54)'
                }
              }}
            />
          );
        } else if (columnType.includes('youtube link')) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <TextField
                fullWidth
                value={cell.value || ''}
                onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)}
                onBlur={() => handleCellBlur(rowIndex, colIndex)}
                onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                placeholder={getPlaceholderText(columnType, rowIndex)}
                autoFocus
                size="small"
                sx={{ '& .MuiInputBase-root': { height: '32px' }, flexGrow: 1 }}
                inputProps={{ 'data-row': rowIndex, 'data-col': colIndex }}
              />
              <IconButton
                size="small"
                onClick={() => handleSearchIconClick(rowIndex)}
                title="Search YouTube"
                sx={{ flexShrink: 0 }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        } else {
          return (
            <TextField
              fullWidth
              value={cell.value || ''}
              onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)}
              onBlur={() => handleCellBlur(rowIndex, colIndex)}
              placeholder={getPlaceholderText(columnType, rowIndex)}
              autoFocus
              size="small"
              sx={{ 
                '& .MuiInputBase-root': {
                  height: '32px'
                },
                '& .MuiInputBase-input': {
                  textAlign: columnType.includes('in point') || columnType.includes('duration') || columnType.includes('peak') || columnType.includes('zoom') ? 'center' : 'left'
                }
              }}
            />
          );
        }
      } else {
        // Non-editing state for row 0
        if (columnType.includes('zoom')) {
          return (
            <Box
              onClick={(e) => {
                console.log('Cell clicked:', { rowIndex, colIndex, columnType });
                e.stopPropagation();
                handleCellEdit(rowIndex, colIndex);
              }}
              sx={{ 
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              <Checkbox 
                checked={cell.value === 'TRUE'} 
                onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.checked ? 'TRUE' : 'FALSE')}
                sx={{ 
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.25rem',
                    color: 'rgba(0, 0, 0, 0.54)'
                  }
                }}
              />
            </Box>
          );
        } else if (columnType.includes('youtube link')) {
          // Apply truncation logic here as well
          const urlValue = cell.value || '';
          const truncatedUrl = urlValue.length > 20 ? `${urlValue.substring(0, 20)}...` : urlValue;
          const displayText = truncatedUrl || getPlaceholderText(columnType, rowIndex);
          const isEmpty = !urlValue; // isEmpty based on original value
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', cursor: 'pointer' }}
                 onClick={() => handleCellEdit(rowIndex, colIndex)}>
              <Typography
                sx={{ 
                    color: isEmpty ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                    flexGrow: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap', 
                 }}
              >
                {/* Display the truncated value or placeholder */}
                {displayText}
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handleSearchIconClick(rowIndex); }}
                title="Search YouTube"
                sx={{ flexShrink: 0 }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        } else {
          return (
            <Box
              onClick={(e) => {
                console.log('Cell clicked:', { rowIndex, colIndex, columnType });
                e.stopPropagation();
                handleCellEdit(rowIndex, colIndex);
              }}
              sx={{ 
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: columnType.includes('in point') || columnType.includes('duration') || columnType.includes('peak') || columnType.includes('zoom') ? 'center' : 'flex-start',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderRadius: '4px'
                }
              }}
            >
              <Typography 
                sx={{ 
                  color: isEmpty ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                  width: '100%',
                  textAlign: columnType.includes('in point') || columnType.includes('duration') || columnType.includes('peak') || columnType.includes('zoom') ? 'center' : 'left',
                  display: 'block'
                }}
              >
                {cell.value || getPlaceholderText(columnType, rowIndex)}
              </Typography>
            </Box>
          );
        }
      }
    }

    // --- Logic for Other Rows (rowIndex > 0) ---
    else {
      // Handle specific columns first
      if (columnType.includes('in point') || columnType.includes('duration')) {
        return (
          <Box
            onClick={() => handleInPointClick(rowIndex, colIndex)}
            sx={{ 
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <Typography 
              sx={{ 
                color: isEmpty ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                textAlign: 'center',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {cell.value || getPlaceholderText(columnType, rowIndex)}
            </Typography>
          </Box>
        );
      } else if (columnType.includes('s3 url')) {
        // Mirror the Vertical URL functionality for S3 URL
        if (cell.value) {
          const strippedUrl = stripProtocol(cell.value);
          const displayText = strippedUrl.length > 20 ? `${strippedUrl.substring(0, 20)}...` : strippedUrl;
          return (
            <Link
              component="button"
              onClick={(e) => {
                e.stopPropagation();
                // Get song and artist for potential upload
                const songColIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
                const artistColIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));
                const song = songColIndex !== -1 ? rows[rowIndex].cells[songColIndex].value : '';
                const artist = artistColIndex !== -1 ? rows[rowIndex].cells[artistColIndex].value : '';

                // Also get the current YouTube URL for this row
                const ytLinkColIndex = headers.findIndex(h => h.toLowerCase().includes('youtube link'));
                const currentYoutubeUrl = ytLinkColIndex !== -1 ? rows[rowIndex].cells[ytLinkColIndex]?.value : '';

                // Get inPoint and duration for timer
                const inPointColIndex = headers.findIndex(h => h.toLowerCase().includes('in point'));
                const durationColIndex = headers.findIndex(h => h.toLowerCase().includes('duration'));
                const inPoint = inPointColIndex !== -1 ? rows[rowIndex].cells[inPointColIndex]?.value || '0' : '0';
                const duration = durationColIndex !== -1 ? rows[rowIndex].cells[durationColIndex]?.value || '0' : '0';

                setEditingS3UrlState({
                  row: rowIndex,
                  col: colIndex,
                  url: cell.value || '',
                  song,
                  artist,
                  currentYoutubeUrl: currentYoutubeUrl || '', // Ensure it's at least an empty string
                  inPoint,
                  duration
                });
              }}
              title={cell.value}
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'left',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {displayText}
            </Link>
          );
        } else {
          // If S3 URL is empty, show just an upload icon like Vertical URL
          return (
            <Box
              sx={{ 
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}
            >
              <IconButton // Changed from Button
                size="small" // IconButton has its own size prop
                onClick={async (e) => {
                  e.stopPropagation();

                    const songColIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
                    const artistColIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));
                  const song = songColIndex !== -1 ? rows[rowIndex].cells[songColIndex].value : '';
                  const artist = artistColIndex !== -1 ? rows[rowIndex].cells[artistColIndex].value : '';

                  let currentYoutubeUrl = '';
                  const ytLinkColIndex = headers.findIndex(h => h.toLowerCase().includes('youtube link'));
                  if (ytLinkColIndex !== -1 && rows[rowIndex] && rows[rowIndex].cells[ytLinkColIndex]) {
                    currentYoutubeUrl = rows[rowIndex].cells[ytLinkColIndex].value || '';
                  } else {
                    console.warn('YouTube Link column not found when opening S3 dialog for empty S3 URL.');
                  }

                  // Get inPoint and duration for timer
                  const inPointColIndex = headers.findIndex(h => h.toLowerCase().includes('in point'));
                  const durationColIndex = headers.findIndex(h => h.toLowerCase().includes('duration'));
                  const inPoint = inPointColIndex !== -1 ? rows[rowIndex].cells[inPointColIndex]?.value || '0' : '0';
                  const duration = durationColIndex !== -1 ? rows[rowIndex].cells[durationColIndex]?.value || '0' : '0';

                    setEditingS3UrlState({
                      row: rowIndex,
                      col: colIndex,
                    url: '', // S3 URL is empty in this case
                    song,
                    artist,
                    currentYoutubeUrl, // Pass the fetched YouTube URL
                    inPoint,
                    duration
                  });
                }}
                // sx removed, IconButton has sensible defaults
              >
                <CloudUploadIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        }
      } else if (columnType.includes('youtube link')) {
        const displayText = cell.value ? formatYoutubeDisplay(cell.value) : '';
        if (cell.isEditing) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <TextField
                fullWidth
                value={cell.value || ''}
                onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)}
                onBlur={() => handleCellBlur(rowIndex, colIndex)}
                onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                placeholder={getPlaceholderText(columnType, rowIndex)}
                size="small"
                sx={{ '& .MuiInputBase-root': { height: '32px' }, flexGrow: 1 }}
                inputProps={{ 'data-row': rowIndex, 'data-col': colIndex }}
              />
              <IconButton
                size="small"
                onClick={() => handleSearchIconClick(rowIndex)}
                title="Search YouTube"
                sx={{ flexShrink: 0 }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Link
              component="button"
              onClick={() => handleCellEdit(rowIndex, colIndex)}
              sx={{
                color: isEmpty ? 'rgba(0, 0, 0, 0.38)' : 'primary.main',
                textDecoration: 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flexGrow: 1,
                textAlign: 'left',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {displayText || getPlaceholderText(columnType, rowIndex)}
            </Link>
            <IconButton
              size="small"
              onClick={() => handleSearchIconClick(rowIndex)}
              title="Search YouTube"
              sx={{ p: 0.25, flexShrink: 0 }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      } else if (columnType.includes('vertical url')) {
        // If Vertical URL exists, show as clickable link
        if (cell.value) {
          const strippedUrl = stripProtocol(cell.value);
          const displayText = strippedUrl.length > 20 ? `${strippedUrl.substring(0, 20)}...` : strippedUrl;
          return (
            <Link
              component="button"
              onClick={(e) => {
                e.stopPropagation();
                // Get song and artist for potential upload
                const songColIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
                const artistColIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));
                const song = songColIndex !== -1 ? rows[rowIndex].cells[songColIndex].value : '';
                const artist = artistColIndex !== -1 ? rows[rowIndex].cells[artistColIndex].value : '';
                const inPointColIndex = headers.findIndex(h => h.toLowerCase().includes('in point'));
                const durationColIndex = headers.findIndex(h => h.toLowerCase().includes('duration'));
                const inPoint = inPointColIndex !== -1 ? rows[rowIndex].cells[inPointColIndex].value || '0' : '0';
                const duration = durationColIndex !== -1 ? rows[rowIndex].cells[durationColIndex].value || '0' : '0';

                setEditingVerticalUrlState({
                  row: rowIndex,
                  col: colIndex,
                  url: cell.value || '',
                  song,
                  artist,
                  inPoint,
                  duration
                });
                setVerticalVideoSessionId(Date.now().toString()); // Generate new session ID for cache-busting
              }}
              title={cell.value}
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'left',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {displayText}
            </Link>
          );
        } else {
          // If Vertical URL is empty, show upload icon
          return (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  // Get song and artist for potential upload
                  const songColIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
                  const artistColIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));
                  const song = songColIndex !== -1 ? rows[rowIndex].cells[songColIndex].value : '';
                  const artist = artistColIndex !== -1 ? rows[rowIndex].cells[artistColIndex].value : '';
                  const inPointColIndex = headers.findIndex(h => h.toLowerCase().includes('in point'));
                  const durationColIndex = headers.findIndex(h => h.toLowerCase().includes('duration'));
                  const inPoint = inPointColIndex !== -1 ? rows[rowIndex].cells[inPointColIndex].value || '0' : '0';
                  const duration = durationColIndex !== -1 ? rows[rowIndex].cells[durationColIndex].value || '0' : '0';

                  setEditingVerticalUrlState({
                    row: rowIndex,
                    col: colIndex,
                    url: '',
                    song,
                    artist,
                    inPoint,
                    duration
                  });
                  setVerticalVideoSessionId(Date.now().toString()); // Generate new session ID for cache-busting
                }}
              >
                <CloudUploadIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        }
      } else if (cell.isEditing) {
        if (columnType.includes('zoom') || columnType.includes('video')) {
          console.log(`[DEBUG] renderCell: Row ${rowIndex}, Col ${colIndex} (${columnType}), IS EDITING. Value: ${cell.value}`); // Added log
          return (
            <Checkbox 
              checked={cell.value === 'TRUE'} 
              onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.checked ? 'TRUE' : 'FALSE')}
              onBlur={() => handleCellBlur(rowIndex, colIndex)}
              sx={{ 
                '& .MuiSvgIcon-root': {
                  fontSize: '1.25rem',
                  color: 'rgba(0, 0, 0, 0.54)'
                }
              }}
            />
          );
        } else if (columnType.includes('song') || columnType.includes('artist')) {
          // Song/Artist editing with multiline support
          return (
            <TextField
              fullWidth
              multiline
              minRows={1}
              maxRows={2}
              value={delimiterToNewline(cell.value || '')}
              onChange={(e) => handleCellChange(rowIndex, colIndex, newlineToDelimiter(e.target.value))}
              onBlur={() => handleCellBlur(rowIndex, colIndex)}
              onKeyDown={(e) => {
                // Allow Enter for newline, use Tab to move to next cell
                if (e.key === 'Escape') {
                  handleCellBlur(rowIndex, colIndex);
                }
              }}
              placeholder={getPlaceholderText(columnType, rowIndex)}
              autoFocus
              size="small"
              sx={{
                '& .MuiInputBase-root': {
                  padding: '4px 8px',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(0, 0, 0, 0.38)',
                  opacity: 1
                }
              }}
              inputProps={{
                'data-row': rowIndex,
                'data-col': colIndex
              }}
            />
          );
        } else {
          return (
            <TextField
              fullWidth
              value={cell.value || ''}
              onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)}
              onBlur={() => handleCellBlur(rowIndex, colIndex)}
              onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
              placeholder={getPlaceholderText(columnType, rowIndex)}
              autoFocus={rowIndex === 0}
              size="small"
              sx={{ 
                '& .MuiInputBase-root': {
                  height: '32px'
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(0, 0, 0, 0.38)',
                  opacity: 1
                }
              }}
              inputProps={{
                'data-row': rowIndex,
                'data-col': colIndex
              }}
            />
          );
        }
      }
      // Handle non-editing display for remaining columns
      else {
        if (columnType.includes('video') || columnType.includes('zoom')) {
          console.log(`[DEBUG] renderCell: Row ${rowIndex}, Col ${colIndex} (${columnType}), NOT editing. Value: ${cell.value}`); // Added log
          return (
            <Box
              onClick={() => handleCellEdit(rowIndex, colIndex)}
              sx={{ /* ... styles ... */ }}
            >
              <Checkbox
                checked={cell.value === 'TRUE'}
                onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.checked ? 'TRUE' : 'FALSE')}
                sx={{ /* ... styles ... */ }}
              />
            </Box>
          );
        } else if (columnType.includes('song') || columnType.includes('artist')) {
          // Song/Artist display with multi-line support
          const parts = splitAtDelimiter(cell.value || '');
          const displayText = cell.value || getPlaceholderText(columnType, rowIndex);
          return (
            <Box
              onClick={() => handleCellEdit(rowIndex, colIndex)}
              sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              {parts.line2 ? (
                <Box sx={{ lineHeight: 1.2, color: isEmpty ? 'rgba(0, 0, 0, 0.38)' : 'inherit' }}>
                  <Box>{parts.line1}</Box>
                  <Box>{parts.line2}</Box>
                </Box>
              ) : (
                <Typography sx={{ color: isEmpty ? 'rgba(0, 0, 0, 0.38)' : 'inherit' }}>
                  {displayText}
                </Typography>
              )}
            </Box>
          );
        } else {
          // Default display for text
          const displayText = cell.value || getPlaceholderText(columnType, rowIndex);
          return (
            <Box
              onClick={() => {
                if (columnType.includes('seek in') && cell.value) {
                  handleSeekInClick(rowIndex, colIndex);
                } else {
                  handleCellEdit(rowIndex, colIndex);
                }
              }}
              sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <Typography
                 sx={{
                   color: isEmpty ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                   width: '100%',
                   textAlign: columnType.includes('in point') || columnType.includes('duration') || columnType.includes('peak') || columnType.includes('weeks on') || columnType.includes('zoom') || columnType.includes('seek in') ? 'center' : 'flex-start',
                   display: 'block',
                   cursor: columnType.includes('seek in') ? 'pointer' : 'inherit',
                   '&:hover': columnType.includes('seek in') ? {
                     textDecoration: 'underline'
                   } : {}
                 }}
              >
                {displayText}
              </Typography>
            </Box>
          );
        }
      }
    }
  };

  const renderRow = (row: Row, rowIndex: number) => {
    const isHighlighted = rowIndex === highlightedRow;
    
    // Debug log for the first data row (index 1) to check the cell count
    if (rowIndex === 1) {
      console.log(`[DEBUG] Row 1 has ${row.cells.length} cells and headers has ${headers.length} items`);
      row.cells.forEach((cell, idx) => console.log(`[DEBUG] Row 1 Cell ${idx}: "${cell.value || 'empty'}" for header "${headers[idx] || 'unknown'}"`));
    }
    
    return (
      <TableRow 
        key={rowIndex}
        ref={isHighlighted ? highlightedRowRef : null}
        sx={{
          backgroundColor: row.isNew 
            ? 'transparent' 
            : (isHighlighted 
                ? 'rgba(255, 255, 0, 0.15)' 
                : (rowIndex % 2 === 0 ? 'rgba(0, 0, 0, 0.04)' : 'transparent')),
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)'
          },
          transition: 'background-color 0.3s ease',
        }}
      >
        <TableCell sx={{ width: '48px', padding: '0 !important' }}>
          {row.isNew ? (
            row.cells.some(cell => cell.value && cell.value.trim() !== '') ? (
              <Box sx={{ display: 'flex', gap: 0, padding: 0 }}>
                <IconButton
                  size="small"
                  onClick={() => handleNewRowSave(rowIndex)}
                  sx={{ color: 'primary.main', padding: '4px' }}
                >
                  ✓
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleNewRowCancel(rowIndex)}
                  sx={{ color: 'error.main', padding: '4px' }}
                >
                  ✕
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ width: '48px' }} /> // Empty box to maintain spacing
            )
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <IconButton
                size="small"
                onClick={() => handleDeleteClick(rowIndex)}
                sx={{ 
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.08)'
                  }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </TableCell>
        {getColumnDisplayOrder().map((colIndex) => {
          const cell = row.cells[colIndex];
          // Hide Zoom column
          if (headers[colIndex]?.toLowerCase().includes('zoom')) return null;
          const isS3UrlColumn = headers[colIndex]?.toLowerCase().includes('s3 url');
          return (
            <TableCell
              key={colIndex}
              align={headers[colIndex].toLowerCase().includes('weeks on') ? 'center' : 'left'}
              sx={{
                fontFamily: "'Open Sans', sans-serif",
                ...(isS3UrlColumn && {
                  width: '160px',
                  minWidth: '160px',
                  maxWidth: '160px',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis'
                }),
                ...getColumnStyle(headers[colIndex])
              }}
            >
              {renderCell(cell, rowIndex, colIndex)}
            </TableCell>
          );
        })}
      </TableRow>
    );
  }

  if (!isInitialized) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography variant="h5" gutterBottom sx={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 600, textTransform: 'uppercase' }}>Video Manager</Typography>
        <Typography color="error" sx={{ fontFamily: "'Open Sans', sans-serif" }}>{error}</Typography>
      </Box>
    );
  }

      return (
        <Box sx={{
      mt: 0,
      mx: 'auto',
      width: '96%',
      maxWidth: '96%',
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" gutterBottom sx={{
          fontFamily: "'Open Sans', sans-serif",
          fontWeight: 600,
          textTransform: 'uppercase',
          mb: 0,
          mt: 2,
          padding: '8px 0'
        }}>Video Manager</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            onClick={() => navigate('/upload-video')}
            disabled={!isSignedIn}
            size="small"
            sx={{
              height: '40px',
              padding: '4px 16px',
              backgroundColor: '#ffffff',
              borderColor: '#000000',
              color: '#000000',
              '&:hover': {
                borderColor: '#000000',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Upload Video
          </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/import-manager')}
          disabled={!isSignedIn}
          size="small"
          sx={{
            height: '40px',
            padding: '4px 16px',
            backgroundColor: '#ffffff',
            borderColor: '#000000',
            color: '#000000',
            '&:hover': {
              borderColor: '#000000',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Import Manager
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/')} // Navigate to root path
          disabled={!isSignedIn}
          size="small"
          sx={{
            height: '40px',
            padding: '4px 16px',
            backgroundColor: '#ffffff',
            borderColor: '#000000',
            color: '#000000',
            '&:hover': {
              borderColor: '#000000',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Charts
        </Button>
        </Box>
      </Box>
      <TableContainer
        component={Paper}
        sx={{
          width: 'fit-content',
          maxWidth: '100%',
          mx: 'auto',
          overflowX: 'auto',
          '& .MuiTable-root': {
            tableLayout: 'fixed',
            display: 'table'
          },
          '& .MuiTableBody-root': {
            display: 'table-row-group'
          },
          '& .MuiTableRow-root': {
            display: 'table-row'
          },
          '& .MuiTableCell-root': {
            display: 'table-cell',
            fontSize: '0.875rem',
            verticalAlign: 'middle',
            fontFamily: "'Open Sans', sans-serif",
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            borderRight: '1px solid rgba(224, 224, 224, 1)',
            padding: '1px 16px',
            '&:last-child': {
              borderRight: 'none'
            }
          }
        }}
      >
        <Table>
          <TableHead sx={{ 
            bgcolor: 'black',
            '& .MuiTableRow-root': {
              height: '48px'
            },
            '& .MuiTableCell-root': {
              fontWeight: '900 !important',
              fontSize: '1.25rem !important',
              fontFamily: "'Open Sans', sans-serif !important",
              height: '48px !important',
              lineHeight: '1.5 !important',
              color: 'white !important',
              whiteSpace: 'nowrap !important',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }
          }}>
            <TableRow>
              <TableCell sx={{ width: '48px', padding: '0 !important' }}></TableCell>
              {getColumnDisplayOrder().map((colIndex) => {
                const header = headers[colIndex];
                // Hide Zoom column
                if (header.toLowerCase().includes('zoom')) return null;
                console.log(`[DEBUG] Rendering header ${colIndex}: "${header}"`);
                return (
                <TableCell
                    key={colIndex}
                  ref={(el: HTMLTableCellElement | null) => { headerRefs.current[header] = el; }}
                  onClick={() => {
                    if (header.toLowerCase().includes('song') ||
                        header.toLowerCase().includes('artist')) {
                      handleSort(header);
                    }
                  }}
                  sx={{
                    fontWeight: 900,
                    whiteSpace: 'nowrap !important',
                    color: 'white',
                    bgcolor: 'black',
                    height: '48px',
                    lineHeight: '1.5',
                    fontFamily: "'Open Sans', sans-serif",
                    position: 'relative',
                    ...((header.toLowerCase().includes('in point') ||
                         header.toLowerCase().includes('duration') ||
                         header.toLowerCase().includes('seek in')) && {
                      fontSize: '1.0rem !important'
                    }),
                    ...getColumnStyle(header)
                    }}
                  >
                    {getDisplayHeaderName(header)}
                  {sortConfig.key === header && (
                    <span style={{ marginLeft: '8px' }}>
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                  {/* Resize handle */}
                  <div
                    onMouseDown={(e) => handleResizeStart(e, header)}
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '6px',
                      cursor: 'col-resize',
                      backgroundColor: resizingColumn === header ? 'rgba(255,255,255,0.5)' : 'transparent',
                      zIndex: 1,
                    }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      if (resizingColumn !== header) {
                        (e.target as HTMLElement).style.backgroundColor = 'transparent';
                      }
                    }}
                  />
                </TableCell>
              )})}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => renderRow(row, rowIndex))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Song</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this song? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog 
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>API Request Preview</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom>
            This is what would be sent to the Google Sheets API:
          </Typography>
          <pre style={{ 
            backgroundColor: '#f5f5f5', 
            padding: '16px', 
            borderRadius: '4px',
            overflow: 'auto',
            maxHeight: '400px'
          }}>
            {JSON.stringify(previewData, null, 2)}
          </pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
          <Button 
            onClick={handleSendJson} 
            variant="contained" 
            color="primary"
          >
            Send JSON
          </Button>
        </DialogActions>
      </Dialog>

      {/* Video Player Dialog */}
      <Dialog
        open={videoPlayerDialogOpen}
        onClose={handleVideoPlayerClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: { xs: '90vw', sm: '85vw', md: '700px' },
            margin: '10px',
            width: '100%',
            overflowX: 'hidden'
          }
        }}
      >
        <DialogTitle>
          Video Player
        </DialogTitle>
        <DialogContent sx={{ 
          overflowX: 'hidden', 
          padding: '16px' 
        }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, pt: 2 }}>
            <TextField
              label="In Point"
              value={currentInPoint} // Use local state
              onChange={handleInPointChange}
              size="small"
              sx={{ 
                width: '100px',
                '& .MuiInputLabel-root': {
                  backgroundColor: 'white',
                  padding: '0 4px',
                  
                }
              }}
            />
            {/* <TextField
              label="Seek In"
              value={currentSeekIn} // Use local state
              onChange={handleSeekInChange}
              size="small"
              sx={{ 
                width: '100px',
                '& .MuiInputLabel-root': {
                  backgroundColor: 'white',
                  padding: '0 4px',
                  
                }
              }}
            /> */}
            <TextField
              label="Duration"
              value={currentDuration} // Use local state for duration
              onChange={handleDurationChange}
              size="small"
              sx={{ 
                width: '100px',
                '& .MuiInputLabel-root': {
                  backgroundColor: 'white',
                  padding: '0 4px',
                  transform: 'translate(14px, -8px) scale(0.75)',
                  '&.Mui-focused': {
                    color: 'primary.main'
                  }
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                }
              }}
            />
          </Box>
          <Box sx={{ 
            width: '100%', 
            maxHeight: '70vh', 
            display: 'flex', 
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#000',
            borderRadius: 1,
            px: { xs: 1, sm: 2, md: 3 }, // Reduce padding further
            py: 2,
            overflow: 'hidden',
            boxSizing: 'border-box'
          }}>
            {!currentVideoUrl ? (
              <Typography color="white" sx={{ p: 2 }}>
                No video URL provided
              </Typography>
            ) : (
              // Standard video player for all URLs
            <video
              id="video-player"
              src={currentVideoUrl}
              controls
              onTimeUpdate={handleMarkInPoint}
              onLoadedMetadata={(e) => {
                  console.log(`[VideoPlayer] Video loaded with URL: ${currentVideoUrl}`);
                const video = e.target as HTMLVideoElement;
                  const inPoint = parseFloat(currentInPoint);
                if (!isNaN(inPoint)) {
                  video.currentTime = inPoint;
                }
              }}
                onError={(e) => {
                  console.error(`[VideoPlayer] Error loading video:`, e);
              }}
              style={{
                width: '100%',
                maxWidth: '100%',
                maxHeight: '60vh',
                objectFit: 'contain'
              }}
            >
              Your browser does not support the video tag.
            </video>
            )}
          </Box>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={handlePreview}
              disabled={isPreviewing || !currentVideoUrl}
            >
              {isPreviewing ? 'Previewing...' : 'Preview'}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleVideoPlayerClose}>Cancel</Button>
          <Button
            onClick={async () => { // Make save async
              if (currentRowIndex !== -1) {
                console.log(`[VideoPlayer Save] Saving In Point: ${currentInPoint}, Seek In: ${currentSeekIn}, Duration: ${currentDuration} for row index: ${currentRowIndex}`);
                // Update local rows state immediately for visual consistency (optional but good)
                const inPointColIndex = headers.findIndex(h => h.toLowerCase().includes('in point'));
                const seekInColIndex = headers.findIndex(h => h.toLowerCase().includes('seek in'));
                const durationColIndex = headers.findIndex(h => h.toLowerCase().includes('duration'));

                setRows(prevRows => {
                   const newRows = [...prevRows];
                   if (newRows[currentRowIndex]) {
                      if (inPointColIndex !== -1 && newRows[currentRowIndex].cells[inPointColIndex]) {
                         newRows[currentRowIndex].cells[inPointColIndex].value = currentInPoint;
                      }
                      if (seekInColIndex !== -1 && newRows[currentRowIndex].cells[seekInColIndex]) {
                         newRows[currentRowIndex].cells[seekInColIndex].value = currentSeekIn;
                      }
                      if (durationColIndex !== -1 && newRows[currentRowIndex].cells[durationColIndex]) {
                         newRows[currentRowIndex].cells[durationColIndex].value = currentDuration;
                      }
                   }
                   return newRows;
                });

                // Now save both values to Google Sheets using handleCellSave
                let saveError = false;
                try {
                if (inPointColIndex !== -1) {
                     await handleCellSave(currentRowIndex, inPointColIndex, currentInPoint);
                   }
                } catch (e) { saveError = true; console.error('Error saving In Point:', e); }

                try {
                   if (seekInColIndex !== -1) {
                     await handleCellSave(currentRowIndex, seekInColIndex, currentSeekIn);
                   }
                 } catch (e) { saveError = true; console.error('Error saving Seek In:', e); }

                try {
                   if (durationColIndex !== -1) {
                     await handleCellSave(currentRowIndex, durationColIndex, currentDuration);
                   }
                 } catch (e) { saveError = true; console.error('Error saving Duration:', e); }

                 if (saveError) {
                   setError('Error saving In Point, Seek In, or Duration to sheet.');
                }
              }
              handleVideoPlayerClose(); // Close dialog after saving
            }}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ScriptManager
        open={scriptDialogOpen}
        onClose={() => setScriptDialogOpen(false)}
        onSave={handleScriptSave}
      />

      {/* YouTube Search Dialog */}
      <YouTubeSearchDialog
        open={searchDialogOpen}
        onClose={handleSearchDialogClose}
        onSelect={handleSearchDialogSelect}
        initialQuery={searchInitialQuery}
      />

      {/* Add Dialog for Editing S3 URL */}
      <Dialog
        open={editingS3UrlState !== null}
        onClose={() => {
          stopHorizontalSmoothTimer();
          setHorizontalVideoCurrentTime(0);
          setEditingS3UrlState(null);
          setVideoSignedUrl('');
          setVideoLoadError(false);
          setIsLoadingSignedUrl(false);
          setS3VideoUploadFile(null); // Reset the upload file state
          setS3UploadProgress(0); // Reset the upload progress
          setDialogInputYoutubeUrl(''); // Reset the YouTube URL input for S3 dialog
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: { xs: '90vw', sm: '85vw', md: '700px' },
            margin: '10px',
            width: '100%',
            overflowX: 'hidden'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h6" component="div">
                S3 Video
              </Typography>
              {editingS3UrlState?.song && editingS3UrlState?.artist && (
                <Typography variant="subtitle1" component="div" sx={{ mt: 1 }}>
                  {editingS3UrlState.artist} - {editingS3UrlState.song}
                </Typography>
              )}
            </Box>

            {/* Timer Counter - only show when video is displayed */}
            {editingS3UrlState?.url && editingS3UrlState?.inPoint && editingS3UrlState?.duration && (
              <Box>
                {(() => {
                  const inPoint = parseFloat(editingS3UrlState.inPoint || '0');
                  const duration = parseFloat(editingS3UrlState.duration || '0');
                  const endTime = inPoint + duration;

                  // Calculate the display time based on current playback position
                  let displayTime = 0;
                  let isOrange = false;

                  if (horizontalVideoCurrentTime < inPoint) {
                    // Before IN point - show negative countdown (orange)
                    displayTime = horizontalVideoCurrentTime - inPoint;
                    isOrange = true;
                  } else if (horizontalVideoCurrentTime >= inPoint && horizontalVideoCurrentTime <= endTime) {
                    // At or after IN point - show positive count (black)
                    displayTime = horizontalVideoCurrentTime - inPoint;
                    isOrange = false;
                  } else {
                    // After OUT point - show final duration (black)
                    displayTime = duration;
                    isOrange = false;
                  }

                  return (
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: '2.5rem',
                        fontWeight: 'bold',
                        fontFamily: 'monospace',
                        color: isOrange ? '#ff6600' : '#000',
                        textShadow: isOrange ? '1px 1px 2px rgba(0,0,0,0.3)' : 'none',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        minWidth: '140px',
                        textAlign: 'center',
                        border: isOrange ? '2px solid #ff6600' : '2px solid #000',
                        backgroundColor: isOrange ? 'rgba(255, 102, 0, 0.1)' : 'rgba(0,0,0,0.05)'
                      }}
                    >
                      {displayTime.toFixed(2)}
                    </Typography>
                  );
                })()}
              </Box>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          overflowX: 'hidden', 
          padding: '16px' 
        }}>
          {/* Video Player */}
          {editingS3UrlState?.url && (
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: '#000',
              borderRadius: 1,
              mb: 2,
              px: { xs: 1, sm: 2, md: 3 }, // Reduce padding further
              py: 2,
              width: '100%',
              justifyContent: 'center',
              overflow: 'hidden', // Ensure content doesn't overflow
              boxSizing: 'border-box' // Include padding in width calculation
            }}>
              {isLoadingSignedUrl && (
                <CircularProgress sx={{ color: 'white', my: 2 }} />
              )}
              
              {!isLoadingSignedUrl && !videoLoadError && (
                <video
                  ref={horizontalVideoRef}
                  key={videoSignedUrl || editingS3UrlState.url} // Force re-render when URL changes
                  src={videoSignedUrl || editingS3UrlState.url}
                  controls
                  preload="metadata"
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    maxHeight: '60vh',
                    objectFit: 'contain',
                    height: 'auto',
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}
                  onLoadedMetadata={(e) => {
                    const video = e.target as HTMLVideoElement;
                    console.log('[S3VideoPlayer] Video loaded successfully!');
                    console.log(`[S3VideoPlayer] Dimensions: ${video.videoWidth}x${video.videoHeight}`);
                    console.log(`[S3VideoPlayer] Duration: ${video.duration}s`);
                    setVideoLoadError(false);

                    // Set the video to start 10 seconds before the IN point
                    if (editingS3UrlState?.inPoint && editingS3UrlState?.duration) {
                      const inPoint = parseFloat(editingS3UrlState.inPoint);
                      const duration = parseFloat(editingS3UrlState.duration);

                      if (!isNaN(inPoint) && !isNaN(duration)) {
                        const startTime = Math.max(0, inPoint - 10);
                        const endTime = inPoint + duration;

                        if (startTime >= video.duration || inPoint >= video.duration) {
                          console.warn(`[S3VideoPlayer] Timing data exceeds video duration. Using video from start.`);
                          video.currentTime = 0;
                          setHorizontalVideoCurrentTime(0);
                        } else {
                          const safeStartTime = Math.min(startTime, video.duration - 1);
                          video.currentTime = safeStartTime;
                          setHorizontalVideoCurrentTime(safeStartTime);
                          console.log(`[S3VideoPlayer] Set video to start time: ${safeStartTime}s (IN: ${inPoint}s, duration: ${duration}s)`);
                        }
                      }
                    } else {
                      setHorizontalVideoCurrentTime(0);
                    }
                  }}
                  onTimeUpdate={(e) => {
                    const video = e.target as HTMLVideoElement;
                    setHorizontalVideoCurrentTime(video.currentTime);

                    // Auto-pause at end time
                    if (editingS3UrlState?.inPoint && editingS3UrlState?.duration) {
                      const inPoint = parseFloat(editingS3UrlState.inPoint);
                      const duration = parseFloat(editingS3UrlState.duration);

                      if (!isNaN(inPoint) && !isNaN(duration) && duration > 0) {
                        const endTime = inPoint + duration;

                        if (inPoint < video.duration) {
                          const safeEndTime = Math.min(endTime, video.duration);
                          if (video.currentTime >= safeEndTime) {
                            video.pause();
                            console.log(`[S3VideoPlayer] Paused at end time: ${safeEndTime}s`);
                          }
                        }
                      }
                    }
                  }}
                  onPlay={(e) => {
                    const video = e.target as HTMLVideoElement;
                    startHorizontalSmoothTimer();

                    // Loop back to start if at/past end
                    if (editingS3UrlState?.inPoint && editingS3UrlState?.duration) {
                      const inPoint = parseFloat(editingS3UrlState.inPoint);
                      const duration = parseFloat(editingS3UrlState.duration);

                      if (!isNaN(inPoint) && !isNaN(duration) && duration > 0) {
                        const endTime = inPoint + duration;
                        const startTime = Math.max(0, inPoint - 10);

                        if (startTime < video.duration && inPoint < video.duration) {
                          const safeStartTime = Math.min(startTime, video.duration - 1);
                          const safeEndTime = Math.min(endTime, video.duration);

                          if (video.currentTime >= safeEndTime) {
                            video.currentTime = safeStartTime;
                            console.log(`[S3VideoPlayer] Looped back to start: ${safeStartTime}s`);
                          }
                        }
                      }
                    }
                  }}
                  onPause={() => {
                    stopHorizontalSmoothTimer();
                  }}
                  onError={async (e) => {
                    console.error('[VideoPlayer] Error loading video:', e);
                    const video = e.target as HTMLVideoElement;
                    console.error('[VideoPlayer] Error code:', video.error?.code);
                    console.error('[VideoPlayer] Error message:', video.error?.message);
                    
                    // If we haven't tried a signed URL yet and we got an error
                    if (!videoSignedUrl && editingS3UrlState?.url) {
                      console.log('[VideoPlayer] Trying to get signed URL...');
                      setIsLoadingSignedUrl(true);
                      
                      try {
                        const response = await fetch(`${SERVER_BASE_URL}/api/get-signed-vertical-url?url=${encodeURIComponent(editingS3UrlState.url)}`);
                        const data = await response.json();
                        
                        if (data.success && data.signedUrl) {
                          console.log('[VideoPlayer] Got signed URL, retrying with it');
                          setVideoSignedUrl(data.signedUrl);
                          setVideoLoadError(false);
                        } else {
                          console.error('[VideoPlayer] Failed to get signed URL:', data.message);
                          setVideoLoadError(true);
                        }
                      } catch (error) {
                        console.error('[VideoPlayer] Error getting signed URL:', error);
                        setVideoLoadError(true);
                      } finally {
                        setIsLoadingSignedUrl(false);
                      }
                    } else {
                      setVideoLoadError(true);
                    }
                  }}
                />
              )}
              
              {!isLoadingSignedUrl && videoLoadError && (
                <Typography color="error" sx={{ my: 2 }}>
                  Error loading video. Please check the URL or permissions.
                </Typography>
              )}
            </Box>
          )}
          
          {/* Option 1: Add URL */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium', color: 'text.secondary' }}>
              Option 1: Add URL
            </Typography>
            <TextField
              label="S3 Video URL"
              value={editingS3UrlState?.url || ''}
              onChange={(e) => setEditingS3UrlState(prev => prev ? {...prev, url: e.target.value} : null)}
              fullWidth
              size="small"
            />
          </Box>

          {/* OR Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
            <Typography variant="body2" sx={{ px: 2, color: 'text.secondary', fontWeight: 'medium' }}>or</Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
          </Box>

          {/* Option 2: Upload File */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium', color: 'text.secondary' }}>
              Option 2: Upload File
            </Typography>
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUploadIcon />}
              fullWidth
            >
              Select File
              <input
                type="file"
                hidden
                accept="video/mp4,video/quicktime"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setS3VideoUploadFile(file);
                    console.log(`[S3 Upload] Selected file: ${file.name} (${file.size} bytes)`);
                  }
                }}
              />
            </Button>
          </Box>

          {/* OR Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
            <Typography variant="body2" sx={{ px: 2, color: 'text.secondary', fontWeight: 'medium' }}>or</Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
          </Box>

          {/* Option 3: Download from YouTube */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium', color: 'text.secondary' }}>
              Option 3: Download from YouTube
            </Typography>
            {/* YouTube URL input if not already set */}
            {editingS3UrlState && !editingS3UrlState.currentYoutubeUrl && (
              <TextField
                label="YouTube Link"
                value={dialogInputYoutubeUrl}
                onChange={(e) => setDialogInputYoutubeUrl(e.target.value)}
                fullWidth
                size="small"
                placeholder="Enter YouTube URL (e.g., https://www.youtube.com/watch?v=...)"
                sx={{ mb: 1 }}
              />
            )}
            <Button
              variant="contained"
              startIcon={<CloudDownloadIcon />}
              fullWidth
              disabled={(
                editingS3UrlState?.currentYoutubeUrl
                  ? false
                  : !(dialogInputYoutubeUrl.trim() && (dialogInputYoutubeUrl.includes('youtube.com/') || dialogInputYoutubeUrl.includes('youtu.be/')))
              )}
              onClick={() => {
                const currentUrlFromState = editingS3UrlState?.currentYoutubeUrl;
                const inputUrlFromDialog = dialogInputYoutubeUrl;
                const urlToUse = currentUrlFromState || inputUrlFromDialog;

                console.log('[DownloadFromYTButton Debug]', {
                  currentUrlFromState,
                  inputUrlFromDialog,
                  urlToUse
                });

                if (editingS3UrlState && editingS3UrlState.row !== null && urlToUse && (urlToUse.includes('youtube.com/') || urlToUse.includes('youtu.be/'))) {
                  // Subtract 1 from rowIndex because rows[0] is the "new blank row" in the UI
                  // So rows[1] = first data row = sheet row 2, etc.
                  // The server expects a 0-based data index, not the UI row index
                  const dataRowIndex = editingS3UrlState.row - 1;
                  console.log(`[VideoManager] Navigating to UploadVideo with YT URL: ${urlToUse} for UI row ${editingS3UrlState.row} (data row index ${dataRowIndex})`);

                  // Build URL with song and artist parameters
                  const params = new URLSearchParams({
                    youtubeUrl: urlToUse,
                    videoManagerRowIndex: dataRowIndex.toString()
                  });

                  // Add song and artist if available
                  if (editingS3UrlState.song) {
                    params.append('song', editingS3UrlState.song);
                  }
                  if (editingS3UrlState.artist) {
                    params.append('artist', editingS3UrlState.artist);
                  }

                  navigate(`/upload-video?${params.toString()}`);
                  setEditingS3UrlState(null); // Close the dialog
                  setDialogInputYoutubeUrl(''); // Reset input
                } else {
                  // This is the error path you're hitting:
                  if (!urlToUse || !(urlToUse.includes('youtube.com/') || urlToUse.includes('youtu.be/'))) {
                    enqueueSnackbar('VideoMan Dialog: Invalid or missing YouTube URL.', { variant: 'warning' });
                  } else {
                    enqueueSnackbar('VideoMan Dialog: Row error for YouTube download.', { variant: 'error' });
                  }
                  console.warn('[VideoManager] Download from YouTube button clicked but conditions not met:', { editingS3UrlState, dialogInputYoutubeUrl });
                }
              }}
            >
              Download from YouTube
            </Button>
          </Box>

          {/* File upload progress - shows after file is selected */}
          {s3VideoUploadFile && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleS3VideoUpload}
                disabled={s3UploadProgress > 0 && s3UploadProgress < 100}
                fullWidth
              >
                Upload to S3
              </Button>
              {s3UploadProgress > 0 && (
                <Box sx={{ width: '100%', mt: 1 }}>
                  <LinearProgress variant="determinate" value={s3UploadProgress} />
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {s3UploadProgress}% Uploaded
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            stopHorizontalSmoothTimer();
            setHorizontalVideoCurrentTime(0);
            setEditingS3UrlState(null);
          }}>Cancel</Button>
          <Button
            onClick={() => editingS3UrlState && handleS3UrlSave(editingS3UrlState.url)}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for editing/viewing Vertical URLs */}
      <Dialog
        open={editingVerticalUrlState !== null}
        onClose={() => {
          setEditingVerticalUrlState(null);
          setVideoSignedUrl('');
          setVideoLoadError(false);
          setIsLoadingSignedUrl(false);
          setVerticalVideoSessionId(''); // Clear session ID when dialog closes
          setVerticalVideoCurrentTime(0);
          setVerticalVideoDuration(0);
          // Reset video to start position if it exists
          if (verticalVideoRef.current) {
            verticalVideoRef.current.currentTime = 0;
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: { xs: '90vw', sm: '85vw', md: '700px' },
            margin: '10px',
            width: '100%',
            overflowX: 'hidden'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" component="div">
                Vertical Video
              </Typography>
              {editingVerticalUrlState?.song && editingVerticalUrlState?.artist && (
                <Typography variant="subtitle1" component="div" sx={{ mt: 1 }}>
                  {editingVerticalUrlState.artist} - {editingVerticalUrlState.song}
                </Typography>
              )}
            </Box>
            
            {/* Timer Counter - only show when video is displayed */}
            {editingVerticalUrlState?.url && Boolean(editingVerticalUrlState?.inPoint) && Boolean(editingVerticalUrlState?.duration) && (
              <Box>
                {(() => {
                  if (!editingVerticalUrlState) return null;
                  const inPoint = parseFloat(editingVerticalUrlState.inPoint || '0');
                  const duration = parseFloat(editingVerticalUrlState.duration || '0');
                  const startTime = Math.max(0, inPoint - 10);
                  const endTime = inPoint + duration;
                  
                  // Calculate the display time based on current playback position
                  let displayTime = 0;
                  let isOrange = false;
                  
                  if (verticalVideoCurrentTime < inPoint) {
                    // Before IN point - show negative countdown (orange)
                    displayTime = verticalVideoCurrentTime - inPoint;
                    isOrange = true;
                  } else if (verticalVideoCurrentTime >= inPoint && verticalVideoCurrentTime <= endTime) {
                    // At or after IN point - show positive count (black)
                    displayTime = verticalVideoCurrentTime - inPoint;
                    isOrange = false;
                  } else {
                    // After OUT point - show final duration (black)
                    displayTime = duration;
                    isOrange = false;
                  }
                  
                  return (
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: '2.5rem',
                        fontWeight: 'bold',
                        fontFamily: 'monospace',
                        color: isOrange ? '#ff6600' : '#000',
                        textShadow: isOrange ? '1px 1px 2px rgba(0,0,0,0.3)' : 'none',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        minWidth: '140px',
                        textAlign: 'center',
                        border: isOrange ? '2px solid #ff6600' : '2px solid #000',
                        backgroundColor: isOrange ? 'rgba(255, 102, 0, 0.1)' : 'rgba(0,0,0,0.05)'
                      }}
                    >
                      {displayTime.toFixed(2)}
                    </Typography>
                  );
                })()}
              </Box>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          overflowX: 'hidden', 
          padding: '16px' 
        }}>
          {/* Video Player */}
          {editingVerticalUrlState?.url && (
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: '#000',
              borderRadius: 1,
              mb: 2,
              px: { xs: 1, sm: 2, md: 3 }, // Reduce padding further
              py: 2,
              width: '100%',
              justifyContent: 'center',
              overflow: 'hidden', // Ensure content doesn't overflow
              boxSizing: 'border-box', // Include padding in width calculation
              position: 'relative' // Enable absolute positioning for timer
            }}>
              {isLoadingSignedUrl && (
                <CircularProgress sx={{ color: 'white', my: 2 }} />
              )}
              
              {!isLoadingSignedUrl && !videoLoadError && (
                <video
                  ref={verticalVideoRef}
                  key={`${videoSignedUrl || editingVerticalUrlState.url}-${verticalVideoSessionId}`} // Force re-render when URL changes and avoid cache
                  src={`${videoSignedUrl || editingVerticalUrlState.url}?cacheBust=${verticalVideoSessionId}`} // Add session ID to bypass cache
                  controls
                  preload="metadata"
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    maxHeight: '60vh',
                    objectFit: 'contain',
                    height: 'auto',
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}
                  
                  onLoadedMetadata={(e) => {
                    const video = e.target as HTMLVideoElement;
                    console.log('[VideoPlayer] Video loaded successfully!');
                    console.log(`[VideoPlayer] Dimensions: ${video.videoWidth}x${video.videoHeight}`);
                    console.log(`[VideoPlayer] Duration: ${video.duration}s`);
                    console.log(`[VideoPlayer] editingVerticalUrlState:`, editingVerticalUrlState);
                    setVideoLoadError(false);
                    setVerticalVideoDuration(video.duration);
                    
                    // Set the video to start 10 seconds before the IN point (or at 0 if IN point < 10)
                    if (editingVerticalUrlState?.inPoint && editingVerticalUrlState?.duration) {
                      const inPoint = parseFloat(editingVerticalUrlState.inPoint);
                      const duration = parseFloat(editingVerticalUrlState.duration);
                      console.log(`[VideoPlayer] Raw IN point: "${editingVerticalUrlState.inPoint}", parsed: ${inPoint}`);
                      console.log(`[VideoPlayer] Raw duration: "${editingVerticalUrlState.duration}", parsed: ${duration}`);
                      
                      if (!isNaN(inPoint) && !isNaN(duration)) {
                        const startTime = Math.max(0, inPoint - 10);
                        const endTime = inPoint + duration;
                        console.log(`[VideoPlayer] Calculated start time: ${startTime}s, end time: ${endTime}s`);
                        console.log(`[VideoPlayer] Video duration: ${video.duration}s`);
                        
                        // Check if the calculated times are within the video's actual duration
                        if (startTime >= video.duration || inPoint >= video.duration) {
                          console.warn(`[VideoPlayer] Timing data exceeds video duration. Using video from start to end.`);
                          video.currentTime = 0;
                          setVerticalVideoCurrentTime(0);
                        } else {
                          // Cap the times to video duration
                          const safeStartTime = Math.min(startTime, video.duration - 1);
                          const safeEndTime = Math.min(endTime, video.duration);
                          console.log(`[VideoPlayer] Safe start time: ${safeStartTime}s, safe end time: ${safeEndTime}s`);
                          video.currentTime = safeStartTime;
                          setVerticalVideoCurrentTime(safeStartTime);
                        }
                        console.log(`[VideoPlayer] Set video to start time: ${video.currentTime}s (IN point: ${inPoint}s, duration: ${duration}s)`);
                      } else {
                        console.warn(`[VideoPlayer] Invalid IN point or duration values: IN=${inPoint}, duration=${duration}`);
                      }
                    } else {
                      console.warn(`[VideoPlayer] Missing IN point or duration in editingVerticalUrlState`);
                    }
                  }}
                  
                  onTimeUpdate={(e) => {
                    const video = e.target as HTMLVideoElement;
                    setVerticalVideoCurrentTime(video.currentTime);
                    
                    if (editingVerticalUrlState?.inPoint && editingVerticalUrlState?.duration) {
                      const inPoint = parseFloat(editingVerticalUrlState.inPoint);
                      const duration = parseFloat(editingVerticalUrlState.duration);
                      
                      if (!isNaN(inPoint) && !isNaN(duration) && duration > 0) {
                        const endTime = inPoint + duration;
                        
                        // Only pause at end time if timing data is valid for this video
                        if (inPoint < video.duration) {
                          const safeEndTime = Math.min(endTime, video.duration);
                          if (video.currentTime >= safeEndTime) {
                            video.pause();
                            console.log(`[VideoPlayer] Paused at end time: ${safeEndTime}s (current: ${video.currentTime}s, IN: ${inPoint}s, duration: ${duration}s)`);
                          }
                        }
                        // If timing data is invalid, let video play normally without constraints
                      }
                    }
                  }}
                  
                  onPlay={(e) => {
                    const video = e.target as HTMLVideoElement;
                    console.log(`[VideoPlayer] onPlay triggered at time: ${video.currentTime}s`);
                    startSmoothTimer(); // Start smooth timer updates
                    if (editingVerticalUrlState?.inPoint && editingVerticalUrlState?.duration) {
                      const inPoint = parseFloat(editingVerticalUrlState.inPoint);
                      const duration = parseFloat(editingVerticalUrlState.duration);
                      
                      if (!isNaN(inPoint) && !isNaN(duration) && duration > 0) {
                        const endTime = inPoint + duration;
                        const startTime = Math.max(0, inPoint - 10);
                        
                        console.log(`[VideoPlayer] onPlay: current=${video.currentTime}s, startTime=${startTime}s, endTime=${endTime}s, videoDuration=${video.duration}s`);
                        
                        // Check if timing data is valid for this video
                        if (startTime >= video.duration || inPoint >= video.duration) {
                          console.warn(`[VideoPlayer] onPlay: Timing data exceeds video duration. Playing from start to end.`);
                          video.currentTime = 0;
                          // Don't constrain playback - let it play the full video
                        } else {
                          // Cap the times to video duration
                          const safeStartTime = Math.min(startTime, video.duration - 1);
                          const safeEndTime = Math.min(endTime, video.duration);
                          
                          // If the video is at or past the end time, seek back to the start time (10 seconds before IN point)
                          if (video.currentTime >= safeEndTime) {
                            video.currentTime = safeStartTime;
                            console.log(`[VideoPlayer] Looped back to start time: ${safeStartTime}s (IN point: ${inPoint}s)`);
                          }
                        }
                      }
                    }
                  }}
                  
                  onPause={() => {
                    console.log('[VideoPlayer] onPause triggered');
                    stopSmoothTimer(); // Stop smooth timer updates when paused
                  }}
                  
                  onError={async (e) => {
                    console.error('[VideoPlayer] Error loading video:', e);
                    const video = e.target as HTMLVideoElement;
                    console.error('[VideoPlayer] Error code:', video.error?.code);
                    console.error('[VideoPlayer] Error message:', video.error?.message);
                    
                    // If we haven't tried a signed URL yet and we got an error
                    if (!videoSignedUrl && editingVerticalUrlState?.url) {
                      console.log('[VideoPlayer] Trying to get signed URL...');
                      setIsLoadingSignedUrl(true);
                      
                      try {
                        const response = await fetch(`${SERVER_BASE_URL}/api/get-signed-vertical-url?url=${encodeURIComponent(editingVerticalUrlState.url)}`);
                        const data = await response.json();
                        
                        if (data.success && data.signedUrl) {
                          console.log('[VideoPlayer] Got signed URL, retrying with it');
                          setVideoSignedUrl(data.signedUrl);
                          setVideoLoadError(false);
                        } else {
                          console.error('[VideoPlayer] Failed to get signed URL:', data.message);
                          setVideoLoadError(true);
                        }
                      } catch (error) {
                        console.error('[VideoPlayer] Error getting signed URL:', error);
                        setVideoLoadError(true);
                      } finally {
                        setIsLoadingSignedUrl(false);
                      }
                    } else {
                      setVideoLoadError(true);
                    }
                  }}
                />
              )}
              
              {!isLoadingSignedUrl && videoLoadError && (
                <Typography color="error" sx={{ my: 2 }}>
                  Error loading video. Please check the URL or permissions.
                </Typography>
              )}
              
            </Box>
          )}
          
          {/* Option 1: Add URL */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium', color: 'text.secondary' }}>
              Option 1: Add URL
            </Typography>
            <TextField
              label="Vertical Video URL"
              value={editingVerticalUrlState?.url || ''}
              onChange={(e) => setEditingVerticalUrlState(prev => prev ? {...prev, url: e.target.value} : null)}
              fullWidth
              size="small"
            />
          </Box>

          {/* OR Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
            <Typography variant="body2" sx={{ px: 2, color: 'text.secondary', fontWeight: 'medium' }}>or</Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
          </Box>

          {/* Option 2: Upload File */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium', color: 'text.secondary' }}>
              Option 2: Upload File
            </Typography>
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUploadIcon />}
              fullWidth
            >
              Select File
              <input
                type="file"
                hidden
                accept="video/mp4,video/quicktime"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setVerticalVideoUploadFile(file);
                    console.log(`[Vertical Upload] Selected file: ${file.name} (${file.size} bytes)`);
                  }
                }}
              />
            </Button>
          </Box>

          {/* File upload progress - shows after file is selected */}
          {verticalVideoUploadFile && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleVerticalVideoUpload}
                disabled={verticalUploadProgress > 0 && verticalUploadProgress < 100}
                fullWidth
              >
                Upload to S3
              </Button>
              {verticalUploadProgress > 0 && (
                <Box sx={{ width: '100%', mt: 1 }}>
                  <LinearProgress variant="determinate" value={verticalUploadProgress} />
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {verticalUploadProgress}% Uploaded
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Option 3: Generate Vertical - only show if horizontal S3 URL exists */}
          {editingVerticalUrlState && (() => {
            const { row } = editingVerticalUrlState;
            const s3UrlColIndex = headers.findIndex(h => h.toLowerCase().includes('s3 url'));
            const s3Url = s3UrlColIndex !== -1 ? rows[row]?.cells[s3UrlColIndex]?.value : null;

            return s3Url ? (
              <>
                {/* OR Divider */}
                <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                  <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
                  <Typography variant="body2" sx={{ px: 2, color: 'text.secondary', fontWeight: 'medium' }}>or</Typography>
                  <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium', color: 'text.secondary' }}>
                    Option 3: Generate from Horizontal
                  </Typography>
                  <Button
                    onClick={handleGenerateVertical}
                    variant="contained"
                    color="primary"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                  >
                    Generate Vertical
                  </Button>
                </Box>
              </>
            ) : null;
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditingVerticalUrlState(null);
            setVideoSignedUrl('');
            setVideoLoadError(false);
            setIsLoadingSignedUrl(false);
            setVerticalVideoSessionId(''); // Clear session ID when dialog closes
            setVerticalVideoCurrentTime(0);
            setVerticalVideoDuration(0);
            // Reset video to start position if it exists
            if (verticalVideoRef.current) {
              verticalVideoRef.current.currentTime = 0;
            }
          }}>Cancel</Button>

          {/* Edit Framing Button - DISABLED FOR NOW
          {editingVerticalUrlState && (() => {
            const { row } = editingVerticalUrlState;
            const s3UrlColIndex = headers.findIndex(h => h.toLowerCase().includes('s3 url'));
            const inPointColIndex = headers.findIndex(h => h.toLowerCase().includes('in point'));
            const durationColIndex = headers.findIndex(h => h.toLowerCase().includes('duration'));

            const s3Url = s3UrlColIndex !== -1 ? rows[row]?.cells[s3UrlColIndex]?.value : null;
            const inPoint = inPointColIndex !== -1 ? rows[row]?.cells[inPointColIndex]?.value || '0' : '0';
            const duration = durationColIndex !== -1 ? rows[row]?.cells[durationColIndex]?.value || '30' : '30';

            // Use the vertical URL that's currently being edited, fall back to S3 URL if needed
            const verticalUrl = editingVerticalUrlState.url || s3Url;

            return verticalUrl ? (
              <Button
                onClick={() => handleEditFraming(verticalUrl, inPoint, duration)}
                variant="outlined"
                color="secondary"
                startIcon={<EditIcon />}
              >
                Edit Framing
              </Button>
            ) : null;
          })()}
          */}

          <Button
            onClick={() => editingVerticalUrlState && handleVerticalUrlSave(editingVerticalUrlState.url)}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Seek Time Dialog */}
      <SeekTimeDialog
        open={seekTimeDialogOpen}
        onClose={handleSeekTimeDialogClose}
        videoUrl={seekTimeDialogVideoUrl}
        videoTitle="Set Default Seek Time"
      />

      {/* Render the Filename Confirmation Dialog */}
      {renderFileNameConfirmationDialog()}

      {/* Video Framing Editor */}
      {framingEditorData && (
        <VideoFramingEditor
          open={framingEditorOpen}
          onClose={handleFramingEditorClose}
          videoUrl={framingEditorData.videoUrl}
          horizontalUrl={framingEditorData.horizontalUrl}
          inPoint={framingEditorData.inPoint}
          duration={framingEditorData.duration}
          onVideoGenerated={handleFramingVideoGenerated}
        />
      )}

    </Box>
  );
}; 

